<template>
    <div id="page-repo-manager" data-page="repo-manager" class="repo-manager-page pb-5">
        <!-- Sprint Selector Row -->
        <b-row class="my-2 align-items-center" v-if="hasRepoSource && hasBacklogConnection">
            <b-col cols="12" class="d-flex justify-content-center">
                <picker-sprints
                    ref="sprintPicker"
                    require-repo
                    connection-context="repo"
                    show-all-time
                    @sprint-picked="onSprintPicked"
                    @sprints-loaded="onSprintsLoaded"
                ></picker-sprints>
            </b-col>
        </b-row>

        <!-- Missing Repository Source Warning Card -->
        <card-missing-source
            v-if="isMissingRepoVisible"
            source-type="repo"
            feature-name="Branch Manager"
            :project="activeProject"
        />

        <!-- Service Unavailable Placeholder -->
        <div v-else-if="isServiceUnavailable" class="text-center py-5 text-muted">
            <i class="bi bi-cloud-slash fa-2x mb-2 d-block text-warning"></i>
            <div class="font-weight-bold mb-1">Service is currently unreachable</div>
            <span class="small">Repositories and branches will be loaded automatically once the service is restored.</span>
        </div>

        <!-- Main Content Area -->
        <template v-else>
            <!-- Header Banner / Stats -->
            <grid-header-banner
                title="Repository Branches"
                subtitle="Remote Git branches, upstream divergence, and sprint scope tracking"
                icon="icon-azure-branch"
                icon-color="text-primary"
                :stats="branchStats"
            />

            <!-- Main Grid Area -->
            <b-overlay :show="loading" rounded="lg" opacity="0.6" class="repo-grid-overlay" :class="{ 'no-sprints': !hasVisibleSprints }">
            <template #overlay>
                <load-spinner text="Loading branches..." />
            </template>

            <!-- Branch Sprint Explanation Banner (Collapsible via Info Button) -->
            <b-collapse v-model="showSprintInfoBanner" class="branches-banner-collapse">
                <b-container
                    fluid
                    v-if="hasVisibleSprints"
                    class="branches-banner-container px-3 mb-2"
                >
                    <b-alert
                        show
                        variant="info"
                        class="branches-sprint-banner d-flex align-items-start align-items-md-center justify-content-between py-2 px-3 mb-0 shadow-sm border-info"
                        data-test="banner-branch-sprint-info"
                    >
                        <div class="d-flex align-items-start align-items-md-center mr-2">
                            <i class="bi bi-info-circle-fill mr-2 mt-1 mt-md-0 flex-shrink-0 text-info" style="font-size: 1.05rem;"></i>
                            <span class="small">
                                <strong>Sprint & Branch Scope:</strong>
                                <template v-if="allTimeFilter">
                                    Showing all repository branches across all time. Select a specific sprint in the picker above to filter branches by commit activity within that sprint's iteration window.
                                </template>
                                <template v-else>
                                    Branches are matched to the selected sprint (<strong>{{ sprintScopeDescription }}</strong>) based on recent commit activity within that sprint's date window. Special branches (<code>main</code>, <code>develop</code>) are excluded by default so you can focus on feature and bugfix branches. Select <strong>All Time</strong> above to view all repository branches.
                                </template>
                            </span>
                        </div>
                        <b-button-close
                            @click="dismissSprintInfoBanner"
                            class="text-reset ml-2 flex-shrink-0"
                            aria-label="Dismiss sprint explanation banner"
                            title="Dismiss banner"
                            data-test="btn-dismiss-sprint-info"
                        />
                    </b-alert>
                </b-container>
            </b-collapse>

            <grid-shell
                class="repo-branches-grid"
                auto-fit-height
                :columns="gridColumns"
                setup-modal-title="Customize Branch Columns & Hygiene Setup"
                :feature-config="branchFeatureConfig"
                @feature-settings-saved="onBranchFeatureSettingsSaved"
                :loading="loading"
                :empty="!loading && !filteredBranches.length"
                :has-active-filters="isAnyFilterActive"
                :empty-title="branchEmptyTitle"
                :empty-message="branchEmptyMessage"
                :empty-suggestions="branchFilterSuggestions"
                :empty-actions="branchEmptyActions"
                @reset-filters="resetToDefaults"
                @empty-action="onEmptyAction"
                min-height="280px"
            >
                <template #filters>
                    <!-- Server-Side Filter Section Header -->
                    <div class="filter-toolbar-label mr-2">
                        <i class="bi bi-funnel mr-1"></i><strong>Filters:</strong>
                    </div>

                    <!-- Repository Wildcard Search Filter (Server-Side) -->
                    <b-input-group size="sm" class="mr-2" style="width: 210px;">
                        <b-input-group-prepend is-text>
                            <i class="bi bi-folder" :class="{ 'text-primary font-weight-bold': isRepoFilterActive }"></i>
                        </b-input-group-prepend>
                        <b-form-input
                            v-model="repoPattern"
                            placeholder="Repo pattern (* wildcard)..."
                            aria-label="Repository search pattern"
                            title="Filter repositories using wildcards (e.g. *finance*, microservice*). Press Enter or click Apply to search."
                            data-test="input-repo-pattern"
                            :disabled="loading"
                            @keydown.enter.prevent="applyFilters"
                            style="height: 31px; font-size: 0.82rem;"
                        ></b-form-input>
                        <b-input-group-append v-if="repoPattern">
                            <b-button
                                size="sm"
                                variant="outline-secondary"
                                @click="clearRepoPattern"
                                aria-label="Clear repository pattern"
                                title="Clear repository pattern"
                                data-test="btn-clear-repo-pattern"
                                :disabled="loading"
                                style="height: 31px;"
                            >
                                <i class="bi bi-x"></i>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>

                    <!-- Branch Search Filter (Server-Side) -->
                    <b-input-group size="sm" class="mr-2" style="width: 190px;">
                        <b-input-group-prepend is-text>
                            <i class="bi bi-search" :class="{ 'text-primary font-weight-bold': isSearchFilterActive }"></i>
                        </b-input-group-prepend>
                        <b-form-input
                            v-model="searchTerm"
                            placeholder="Branch pattern (* wildcard)..."
                            aria-label="Branch search pattern"
                            title="Filter branches by name or wildcard. Press Enter or click Apply to search."
                            data-test="input-branch-search"
                            :disabled="loading"
                            @keydown.enter.prevent="applyFilters"
                            style="height: 31px; font-size: 0.82rem;"
                        ></b-form-input>
                        <b-input-group-append v-if="searchTerm">
                            <b-button
                                size="sm"
                                variant="outline-secondary"
                                @click="clearSearch"
                                aria-label="Clear branch search"
                                title="Clear branch search"
                                data-test="btn-clear-branch-search"
                                :disabled="loading"
                                style="height: 31px;"
                            >
                                <i class="bi bi-x"></i>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>

                    <!-- Author Filter Chip (Single Select: Me, Actual Team Name, Member1...) -->
                    <filter-author-dropdown
                        v-model="selectedAuthor"
                        :project-id="activeProject ? activeProject.id : null"
                        :project="activeProject"
                        class="mr-2"
                        :disabled="loading"
                        data-test="filter-chip-author"
                    />

                    <!-- Status Filter Chip (Compact multi-select) -->
                    <filter-multi-select
                        v-model="statusFilter"
                        label="Status"
                        icon="bi bi-funnel"
                        :options="statusOptions"
                        :allow-empty="false"
                        all-label="All Statuses"
                        none-label="None"
                        class="mr-2"
                        :disabled="loading"
                        data-test="filter-chip-status"
                    />

                    <!-- Special Branches Filter Chip (Server-Side) -->
                    <filter-multi-select
                        v-model="specialFilter"
                        label="Special"
                        icon="bi bi-star"
                        :options="specialOptions"
                        all-label="All Included"
                        none-label="Excluded"
                        count-format="+{count} Included"
                        active-condition="any"
                        all-text="All"
                        clear-text="None"
                        class="mr-2"
                        :disabled="loading"
                        data-test="filter-chip-special"
                    />

                    <!-- Action Buttons aligned to the right -->
                    <div class="filter-toolbar-actions ml-auto d-flex align-items-center">
                        <!-- Apply Button -->
                        <b-button
                            size="sm"
                            variant="primary"
                            class="mr-2 font-weight-bold"
                            :class="{ 'btn-apply-pulse': hasUnappliedFilters && !loading }"
                            @click="applyFilters"
                            :disabled="loading || !statusFilter || statusFilter.length === 0"
                            title="Apply filters and reload branches from server"
                            data-test="btn-apply-filters"
                            :data-dirty="hasUnappliedFilters ? 'true' : 'false'"
                            :data-state="loading ? 'loading' : 'idle'"
                        >
                            <i class="bi bi-check-circle mr-1"></i>Apply
                        </b-button>

                        <!-- Reset to Defaults Button -->
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            class="mr-2"
                            @click="resetToDefaults"
                            :disabled="loading"
                            title="Reset all filters to defaults"
                            data-test="btn-reset-filters"
                        >
                            <i class="bi bi-arrow-counterclockwise mr-1"></i>Reset to Defaults
                        </b-button>

                        <!-- Sprint & Branch Scope Info Toggle Button (Rightmost, next to Apply) -->
                        <b-button
                            size="sm"
                            :variant="showSprintInfoBanner ? 'info' : 'outline-info'"
                            class="btn-sprint-info-toggle"
                            @click="toggleSprintInfoBanner"
                            :aria-expanded="showSprintInfoBanner ? 'true' : 'false'"
                            aria-label="Toggle sprint and branch scope explanation"
                            :title="showSprintInfoBanner ? 'Hide sprint & branch scope information' : 'Show sprint & branch scope information'"
                            data-test="btn-toggle-sprint-info"
                        >
                            <i class="bi bi-info-circle-fill text-white" v-if="showSprintInfoBanner"></i>
                            <i class="bi bi-info-circle" v-else></i>
                        </b-button>
                    </div>

                </template>
                <template #view>
                    <!-- Grouping chip (2 options: No Grouping, By Repository) -->
                    <b-dropdown
                        size="sm"
                        :variant="groupMode !== 'none' ? 'primary' : 'outline-secondary'"
                        no-caret
                        boundary="window"
                        class="grid-filter-chip mr-2"
                        data-test="filter-chip-grouping"
                    >
                        <template #button-content>
                            <i class="bi bi-folder2-open mr-1"></i>
                            <span class="font-weight-bold mr-1">Grouping:</span>
                            <span>{{ groupModeLabel }}</span>
                            <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                        </template>
                        <b-dropdown-item-button :active="groupMode === 'none'" @click="groupMode = 'none'" data-test="grouping-option-none">
                            <i class="bi bi-list-ul mr-2"></i>No Grouping
                        </b-dropdown-item-button>
                        <b-dropdown-item-button :active="groupMode === 'repo'" @click="groupMode = 'repo'" data-test="grouping-option-repo">
                            <i class="icon-azure-branch mr-2"></i>By Repository
                        </b-dropdown-item-button>
                    </b-dropdown>
                </template>
                <template #view-actions>
                    <!-- View-Only Mode Badge -->
                    <span v-if="isViewOnlyMode" class="badge badge-warning text-dark px-2 py-1 mr-2 font-weight-bold"
                        data-test="badge-view-only"
                        v-b-tooltip.hover title="Free tier is in View-Only mode. Upgrading to Pro Plan unlocks branch deletion and management actions.">
                        <i class="bi bi-eye mr-1"></i>View-Only Mode
                    </span>
                </template>
                <template v-if="false">
                    <div class="filter-toolbar-group filter-toolbar-view d-flex align-items-center flex-wrap w-100">
                        <div class="filter-toolbar-label mr-2">
                            <i class="bi bi-eye mr-1"></i><strong>View:</strong>
                        </div>

                        <!-- Grouping chip (2 options: No Grouping, By Repository) -->
                        <b-dropdown
                            size="sm"
                            :variant="groupMode !== 'none' ? 'primary' : 'outline-secondary'"
                            no-caret
                            boundary="window"
                            class="grid-filter-chip mr-2"
                            data-test="filter-chip-grouping"
                        >
                            <template #button-content>
                                <i class="bi bi-folder2-open mr-1"></i>
                                <span class="font-weight-bold mr-1">Grouping:</span>
                                <span>{{ groupModeLabel }}</span>
                                <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                            </template>
                            <b-dropdown-item-button :active="groupMode === 'none'" @click="groupMode = 'none'" data-test="grouping-option-none">
                                <i class="bi bi-list-ul mr-2"></i>No Grouping
                            </b-dropdown-item-button>
                            <b-dropdown-item-button :active="groupMode === 'repo'" @click="groupMode = 'repo'" data-test="grouping-option-repo">
                                <i class="icon-azure-branch mr-2"></i>By Repository
                            </b-dropdown-item-button>
                        </b-dropdown>

                        <!-- View-Only Mode Badge -->
                        <span v-if="isViewOnlyMode" class="badge badge-warning text-dark px-2 py-1 ml-2 font-weight-bold"
                            data-test="badge-view-only"
                            v-b-tooltip.hover title="Free tier is in View-Only mode. Upgrading to Pro Plan unlocks branch deletion and management actions.">
                            <i class="bi bi-eye mr-1"></i>View-Only Mode
                        </span>

                        <!-- Counter Pill placed cleanly inside View row -->
                        <div class="ml-auto d-flex align-items-center">
                            <b-button
                                v-if="primaryRepoAdoBranchesUrl"
                                :href="primaryRepoAdoBranchesUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="sm"
                                variant="outline-primary"
                                class="mr-2 py-0 px-2 d-inline-flex align-items-center"
                                style="height: 24px; font-size: 0.76rem;"
                                title="Open repository branches directly in Azure DevOps"
                                data-test="btn-open-ado-branches"
                            >
                                <i class="bi bi-box-arrow-up-right mr-1"></i>Open in Azure DevOps
                            </b-button>
                            <b-badge variant="secondary" class="py-1 px-2 font-weight-500">
                                {{ filteredBranches.length }} {{ filteredBranches.length === 1 ? 'branch' : 'branches' }}
                            </b-badge>
                        </div>
                    </div>
                </template>

                <!-- Grid Column Headers -->
                <template #header-collapser v-if="groupMode !== 'none'">
                    <b-button @click="toggleCollapseAll" variant="secondary" size="sm"
                        aria-label="Toggle Collapse ALL"
                        title="Toggle Collapse ALL"
                        data-test="btn-toggle-collapse-all"
                        v-b-popover.hover.top.noninteractive="actionHoverPop('Toggle Collapse ALL')">
                        <i class="bi bi-chevron-right row-expand-icon" :class="{ 'is-expanded': isAllCollapsesOpen }"></i>
                    </b-button>
                </template>
                <template #header-repository v-if="!isGroupedByRepo">
                    <span>Repository</span>
                </template>
                <template #header-name>
                    <span>Branch</span>
                </template>
                <template #header-compare>
                    <div class="text-center">Behind / Ahead</div>
                </template>
                <template #header-status>
                    <div class="text-center">Status</div>
                </template>
                <template #header-commit>
                    <span>Last Commit</span>
                </template>
                <template #header-author>
                    <span>Author</span>
                </template>
                <template #header-action>
                    <div class="text-center">Action</div>
                </template>

                <!-- Branch Group Cards -->
                <div v-for="(group, gIndex) in visibleGroups" :key="group.groupId">
                    <b-card style="margin: 8px 0px;">
                        <!-- Group Header Row (shown when not flat) -->
                        <b-row class="my-1 grid-row grid-project-row" v-if="!group.isFlat">
                            <b-col class="px-1" style="max-width: 40px; flex: 0 0 40px;">
                                <b-button variant="secondary" @click="toggleCollapseSingle(gIndex)" size="sm"
                                    aria-label="Toggle Collapse"
                                    title="Toggle Collapse"
                                    data-test="btn-toggle-collapse-group"
                                    v-b-popover.hover.top.noninteractive="actionHoverPop('Toggle Collapse')"
                                    v-if="group.branches.length > 0">
                                    <i class="bi bi-chevron-right row-expand-icon" :class="{ 'is-expanded': !collapsesStates[gIndex] }"></i>
                                </b-button>
                            </b-col>
                            <b-col class="px-2 d-flex align-items-center">
                                <i :class="group.icon || 'bi bi-folder2-open'" class="mr-2 text-primary"></i>
                                <nav-link-button
                                    v-if="group.branches.length > 0"
                                    :href="getRepoAdoUrl(group.branches[0])"
                                    :text="group.groupName"
                                    copyable
                                    :copy-value="group.groupName"
                                    copy-title="Copy repository name"
                                    title="Open repository branches in Azure DevOps"
                                    size="sm"
                                    class="mr-2 font-weight-bold"
                                />
                                <strong v-else class="mr-2">{{ group.groupName }}</strong>
                                <b-badge variant="secondary" pill>{{ group.branches.length }}</b-badge>
                            </b-col>
                        </b-row>

                        <!-- Collapsible Branch Rows -->
                        <b-collapse :visible="group.isFlat || !collapsesStates[gIndex]">
                            <div v-for="b in group.branches" :key="`${b.repositoryId}-${b.name}`" class="repo-branch-item-wrap">
                                <b-row align-h="start" class="my-1 grid-row repo-branch-row" :class="{ 'is-default': b.isDefault, 'can-delete': canDeleteBranch(b) }" data-test="grid-row-branch" :data-test-id="b.name">
                                    <!-- Spacer for collapser if grouped -->
                                    <b-col v-if="!group.isFlat" class="px-1 repo-col-spacer" style="max-width: 40px; flex: 0 0 40px;"></b-col>

                                    <!-- Repository Column (shown when not grouped by repo) -->
                                    <b-col v-if="!isGroupedByRepo" class="px-2 repo-col-repo d-flex align-items-center overflow-hidden">
                                        <nav-link-button
                                            :href="getRepoAdoUrl(b)"
                                            :text="b.repositoryName || '–'"
                                            icon="bi bi-folder text-muted"
                                            copyable
                                            :copy-value="b.repositoryName"
                                            copy-title="Copy repository name"
                                            title="Open repository branches in Azure DevOps"
                                            size="sm"
                                            class="mr-2 text-truncate"
                                        />
                                    </b-col>

                                    <!-- Branch Name Column (nav-link-button) -->
                                    <b-col class="px-2 repo-col-name d-flex align-items-center overflow-hidden">
                                        <nav-link-button
                                            :href="getBranchAdoUrl(b)"
                                            :text="b.name"
                                            icon="icon-azure-branch text-primary"
                                            copyable
                                            :copy-value="b.name"
                                            copy-title="Copy branch name"
                                            title="Open branch in Azure DevOps"
                                            size="sm"
                                            class="mr-2 text-truncate"
                                        />
                                        <b-badge
                                            v-if="(!branchFeatureSettings || (branchFeatureSettings.enabled !== false && branchFeatureSettings.showSpecialBadges !== false)) && getSpecialBranchType(b)"
                                            :variant="getSpecialBranchBadgeVariant(getSpecialBranchType(b))"
                                            class="mr-1 flex-shrink-0"
                                        >
                                            {{ getSpecialBranchLabel(b) }}
                                        </b-badge>
                                        <b-badge v-if="(!branchFeatureSettings || (branchFeatureSettings.enabled !== false && branchFeatureSettings.showMineBadge !== false)) && isBranchMine(b) && selectedAuthor !== 'me'" variant="info" class="mr-1 flex-shrink-0">mine</b-badge>
                                    </b-col>

                                    <!-- Behind / Ahead -->
                                    <b-col class="px-2 repo-col-diff d-flex align-items-center justify-content-center">
                                        <div v-if="!b.isDefault && (!branchFeatureSettings || (branchFeatureSettings.enabled !== false && branchFeatureSettings.showAheadBehindPills !== false))" class="d-flex align-items-center branch-diff-group" data-test="btn-branch-commits">
                                            <span
                                                role="button"
                                                tabindex="0"
                                                class="branch-diff-pill branch-diff-ahead text-success font-weight-bold mr-1 d-inline-flex align-items-center"
                                                data-test="badge-branch-ahead"
                                                v-b-tooltip.hover.top="`${b.aheadCount} ahead of default branch — click to view commits`"
                                                @click.stop="openBranchCommitsModal(b, 'ahead')"
                                                @keydown.enter.prevent="openBranchCommitsModal(b, 'ahead')"
                                                @keydown.space.prevent="openBranchCommitsModal(b, 'ahead')"
                                            >
                                                <i class="bi bi-arrow-up-short"></i>{{ b.aheadCount }}
                                            </span>
                                            <span
                                                role="button"
                                                tabindex="0"
                                                class="branch-diff-pill branch-diff-behind text-danger font-weight-bold d-inline-flex align-items-center"
                                                data-test="badge-branch-behind"
                                                v-b-tooltip.hover.top="`${b.behindCount} behind default branch — click to view commits`"
                                                @click.stop="openBranchCommitsModal(b, 'behind')"
                                                @keydown.enter.prevent="openBranchCommitsModal(b, 'behind')"
                                                @keydown.space.prevent="openBranchCommitsModal(b, 'behind')"
                                            >
                                                <i class="bi bi-arrow-down-short"></i>{{ b.behindCount }}
                                            </span>
                                        </div>
                                        <span v-else class="text-muted">–</span>
                                    </b-col>

                                    <!-- Status -->
                                    <b-col class="px-2 repo-col-status d-flex align-items-center justify-content-center">
                                        <b-badge v-if="b.isDefault" variant="primary">Default</b-badge>
                                        <b-badge v-else-if="b.isMerged" variant="info">Merged</b-badge>
                                        <b-badge v-else-if="b.isStale" variant="warning">Stale</b-badge>
                                        <b-badge v-else variant="success">Active</b-badge>
                                    </b-col>

                                    <!-- Last Commit -->
                                    <b-col class="px-2 repo-col-commit d-flex flex-column justify-content-center overflow-hidden">
                                        <div class="mobile-col-caption d-lg-none">Last Commit</div>
                                        <div class="text-truncate" v-b-tooltip.hover.top="b.lastCommitMessage ? `${formatDateFull(b.lastCommitDate)}: ${b.lastCommitMessage}` : formatDateFull(b.lastCommitDate)">
                                            <span class="text-muted small">{{ formatDateRelative(b.lastCommitDate) }}</span>
                                            <div v-if="b.lastCommitMessage" class="text-muted text-truncate" style="opacity: 0.8; font-size: 0.72rem; line-height: 1.2;">
                                                {{ b.lastCommitMessage }}
                                            </div>
                                        </div>
                                    </b-col>

                                    <!-- Author -->
                                    <b-col class="px-2 repo-col-author d-flex align-items-center overflow-hidden">
                                        <i class="mr-1 flex-shrink-0" :class="isBranchMine(b) ? 'bi bi-person-check-fill text-info font-weight-bold' : 'bi bi-person text-muted'"></i>
                                        <span class="text-truncate small" :class="isBranchMine(b) ? 'font-weight-bold text-info' : ''" :title="b.lastCommitAuthorName || 'Unknown'">
                                            {{ b.lastCommitAuthorName || 'Unknown' }}
                                        </span>
                                        <b-badge v-if="isBranchMine(b)" variant="info" class="ml-1 px-1 py-0 extra-small font-weight-bold flex-shrink-0" data-test="badge-author-self">You</b-badge>
                                    </b-col>

                                    <!-- Action -->
                                    <b-col class="px-1 repo-col-buttons action-buttons d-flex align-items-center justify-content-center">
                                        <grid-button-section
                                            v-if="!b.isDefault && branchActionButtons(b).some(btn => btn.visible !== false)"
                                            :buttons="branchActionButtons(b)"
                                            @action="onBranchAction(b, $event)"
                                        />
                                        <span v-else class="text-muted">–</span>
                                    </b-col>
                                </b-row>
                            </div>
                        </b-collapse>
                    </b-card>
                </div>
            </grid-shell>
        </b-overlay>
        </template>

        <!-- High-Alert Branch Deletion Confirmation Modal -->
        <b-modal
            id="modal-delete-branch-confirm"
            v-model="showDeleteBranchModal"
            title="High-Alert: Delete Remote Branch"
            size="lg"
            header-bg-variant="danger"
            header-text-variant="white"
            dialog-class="modal-dialog-elevated"
            hide-footer
            no-close-on-backdrop
            data-test="modal-delete-branch"
        >
            <div v-if="branchPendingDelete" class="p-2">
                <b-alert variant="danger" show class="d-flex align-items-start mb-3 shadow-sm border-danger">
                    <i class="bi bi-exclamation-triangle-fill text-danger mr-3 flex-shrink-0" style="font-size: 1.5rem; line-height: 1.2;"></i>
                    <div>
                        <strong class="d-block mb-1">Permanent & Irreversible Destruction</strong>
                        <span class="small">
                            Deleting a remote branch permanently erases its ref from the repository. Any unmerged commits, tags, or work unique to this branch will be permanently lost and cannot be recovered.
                        </span>
                    </div>
                </b-alert>

                <div class="delete-branch-details p-3 rounded mb-3 border">
                    <div class="d-flex justify-content-between align-items-center py-1">
                        <span class="text-muted small">Target Branch:</span>
                        <span class="badge badge-danger font-weight-bold px-2 py-1">
                            <i class="icon-azure-branch mr-1"></i>{{ branchPendingDelete.name }}
                        </span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center py-1">
                        <span class="text-muted small">Repository:</span>
                        <strong class="small font-weight-bold text-body">{{ branchPendingDelete.repositoryName || 'Active Repository' }}</strong>
                    </div>
                    <div v-if="getRepoAdoUrl(branchPendingDelete)" class="d-flex justify-content-between align-items-center py-1">
                        <span class="text-muted small">Azure DevOps Deep Link:</span>
                        <a
                            :href="getBranchAdoUrl(branchPendingDelete) || getRepoAdoUrl(branchPendingDelete)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="small font-weight-bold text-primary d-inline-flex align-items-center"
                            data-test="link-open-ado-branches"
                        >
                            <i class="bi bi-box-arrow-up-right mr-1"></i>View Branch in Azure DevOps
                        </a>
                    </div>
                    <div v-if="branchPendingDelete.aheadCount || branchPendingDelete.behindCount" class="d-flex justify-content-between align-items-center py-1">
                        <span class="text-muted small">Sync Status:</span>
                        <span class="extra-small">
                            <span v-if="branchPendingDelete.aheadCount" class="text-success font-weight-bold mr-2">
                                +{{ branchPendingDelete.aheadCount }} ahead
                            </span>
                            <span v-if="branchPendingDelete.behindCount" class="text-secondary">
                                -{{ branchPendingDelete.behindCount }} behind
                            </span>
                        </span>
                    </div>
                </div>

                <!-- Ahead Commits Section -->
                <div v-if="branchPendingDelete.aheadCount > 0" class="ahead-commits-card p-3 rounded mb-3 border">
                    <div class="mb-2">
                        <div class="d-flex align-items-center flex-wrap mb-1">
                            <span class="small font-weight-bold text-warning d-flex align-items-center">
                                <i class="bi bi-exclamation-triangle-fill mr-2"></i>
                                Unmerged Commits at Risk:
                                <b-badge variant="warning" class="ml-2 px-2 py-1 font-weight-bold text-dark">
                                    +{{ aheadCommits.length || branchPendingDelete.aheadCount }} ahead
                                </b-badge>
                            </span>
                        </div>
                        <div class="extra-small text-muted">
                            Work unique to this branch that will be permanently lost
                        </div>
                    </div>

                    <div v-if="loadingAheadCommits" class="text-center py-2" data-state="loading">
                        <load-spinner text="Retrieving unmerged commits..." size="sm" inline variant="warning" />
                    </div>

                    <div v-else-if="aheadCommitsError" class="text-center py-2 extra-small text-danger">
                        <span>{{ aheadCommitsError }}</span>
                        <b-button size="sm" variant="link" class="p-0 ml-2 extra-small" @click="loadAheadCommitsAsync(branchPendingDelete)">Retry</b-button>
                    </div>

                    <div v-else-if="aheadCommits.length > 0" class="ahead-commits-list">
                        <div
                            v-for="c in aheadCommits"
                            :key="c.commitId"
                            class="ahead-commit-row d-flex align-items-center justify-content-between py-2 px-2 rounded mb-1"
                            :class="{ 'ahead-commit-row--mine': isCommitMine(c), 'ahead-commit-row--other': !isCommitMine(c) }"
                            :data-author-self="isCommitMine(c) ? 'true' : 'false'"
                            data-test="ahead-commit-row"
                        >
                            <div class="d-flex align-items-center text-truncate mr-2" style="min-width: 0;">
                                <nav-link-button
                                    :href="getCommitAdoUrl(c)"
                                    :text="c.shortCommitId || c.commitId"
                                    icon="bi bi-hash"
                                    copyable
                                    :copy-value="c.commitId"
                                    copy-title="Copy full commit SHA"
                                    title="Open commit in Azure DevOps"
                                    size="sm"
                                    class="mr-2 flex-shrink-0"
                                    data-test="link-commit-sha"
                                />
                                <span class="small text-truncate" :class="isCommitMine(c) ? 'font-weight-bold text-body' : 'text-muted'" :title="c.comment">
                                    {{ c.comment || 'No commit message' }}
                                </span>
                            </div>
                            <div class="d-flex align-items-center flex-shrink-0 extra-small text-muted">
                                <template v-if="c.authorName">
                                    <b-badge v-if="isCommitMine(c)" variant="info" class="mr-1 px-1 py-0 extra-small font-weight-bold" data-test="badge-commit-mine">You</b-badge>
                                    <span class="mr-2 d-none d-sm-inline" :class="isCommitMine(c) ? 'font-weight-bold text-info' : 'opacity-75'" :title="c.authorEmail">
                                        <i :class="isCommitMine(c) ? 'bi bi-person-check-fill mr-1' : 'bi bi-person mr-1'"></i>{{ c.authorName }}
                                    </span>
                                </template>
                                <span v-if="c.authorDate" :title="formatDateFull(c.authorDate)">
                                    <i class="bi bi-clock mr-1"></i>{{ formatDateRelative(c.authorDate) }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div v-else class="text-center py-2 extra-small text-muted">
                        <span>Ahead count indicates +{{ branchPendingDelete.aheadCount }} commit(s), but commit details could not be resolved from comparison.</span>
                    </div>
                </div>

                <div v-else class="ahead-commits-card p-3 rounded mb-3 border">
                    <div class="d-flex align-items-center small text-success">
                        <i class="bi bi-check-circle-fill mr-2 font-weight-bold"></i>
                        <span><strong>Fully merged:</strong> This branch has 0 commits ahead. No unmerged changes will be lost by deleting it.</span>
                    </div>
                </div>

                <!-- Associated Pull Requests Section (Issue #18) -->
                <div class="ahead-commits-card p-3 rounded mb-3 border" data-test="delete-modal-pr-section">
                    <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
                        <span class="small font-weight-bold text-body d-flex align-items-center">
                            <i class="icon-azure-pr mr-2 text-info"></i>
                            Pull Request History &amp; State
                            <b-badge variant="secondary" class="ml-2 px-2 py-1">{{ deleteModalPrs.length }}</b-badge>
                        </span>
                        <span class="extra-small text-muted">Past &amp; active PRs for this branch</span>
                    </div>

                    <div v-if="loadingDeleteModalPrs" class="text-center py-2" data-state="loading" data-test="delete-modal-pr-loading">
                        <load-spinner text="Checking associated pull requests..." size="sm" inline variant="info" />
                    </div>

                    <div v-else-if="deleteModalPrsError" class="text-center py-2 extra-small text-danger">
                        <span>{{ deleteModalPrsError }}</span>
                        <b-button size="sm" variant="link" class="p-0 ml-2 extra-small" @click="loadBranchPullRequestsAsync(branchPendingDelete)">Retry</b-button>
                    </div>

                    <div v-else-if="deleteModalPrs.length > 0">
                        <div
                            v-if="hasAbandonedDeleteModalPr"
                            class="alert alert-warning py-2 px-3 mb-2 small d-flex align-items-start"
                            data-test="alert-abandoned-pr-warning"
                        >
                            <i class="bi bi-exclamation-octagon-fill text-danger mr-2 mt-1 flex-shrink-0"></i>
                            <div>
                                <strong>Abandoned Pull Request Notice:</strong> One or more pull requests from this branch were <strong>Abandoned</strong> without merging. Verify whether these changes were superseded before deleting the branch.
                            </div>
                        </div>

                        <div
                            v-for="pr in deleteModalPrs"
                            :key="pr.id"
                            class="ahead-commit-row d-flex align-items-center justify-content-between py-2 px-2 rounded mb-1"
                            :class="{ 'pr-row--abandoned': (pr.status || '').toLowerCase() === 'abandoned' }"
                            data-test="delete-modal-pr-row"
                        >
                            <div class="d-flex align-items-center text-truncate mr-2" style="min-width: 0;">
                                <nav-link-button
                                    :href="pr.url"
                                    :text="`#${pr.id}`"
                                    icon="icon-azure-pr"
                                    title="Open Pull Request in Azure DevOps"
                                    size="sm"
                                    class="mr-2 flex-shrink-0"
                                    data-test="link-delete-modal-pr"
                                />
                                <span class="small text-truncate text-body font-weight-bold mr-2" :title="pr.title">
                                    {{ pr.title || `Pull Request #${pr.id}` }}
                                </span>
                                <span v-if="pr.targetBranch" class="extra-small text-muted flex-shrink-0">
                                    &rarr; {{ pr.targetBranch }}
                                </span>
                            </div>
                            <div class="d-flex align-items-center flex-shrink-0">
                                <b-badge
                                    :variant="getPrStatusBadgeVariant(pr.status)"
                                    class="px-2 py-1 extra-small font-weight-bold text-uppercase"
                                    data-test="badge-delete-modal-pr-status"
                                >
                                    <i v-if="(pr.status || '').toLowerCase() === 'abandoned'" class="bi bi-x-octagon-fill mr-1"></i>
                                    <i v-else-if="(pr.status || '').toLowerCase() === 'completed'" class="bi bi-check-circle-fill mr-1"></i>
                                    {{ pr.status || 'Active' }}
                                </b-badge>
                            </div>
                        </div>
                    </div>

                    <div v-else class="text-center py-1 extra-small text-muted" data-test="delete-modal-pr-empty">
                        <span>No pull requests have been created from this branch.</span>
                    </div>
                </div>

                <!-- Branch Compare Section (Issue #19: Compare to Default & Compare Branches with Accordion & Inline/Side-by-Side Diff) -->
                <div class="ahead-commits-card p-3 rounded mb-3 border" data-test="delete-modal-compare-section">
                    <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
                        <div>
                            <span class="small font-weight-bold text-body d-flex align-items-center">
                                <i class="bi bi-file-earmark-diff mr-2 text-primary"></i>
                                Compare Branch File Changes
                            </span>
                            <div class="extra-small text-muted">
                                Inspect changed files against the default or integration branch before deleting
                            </div>
                        </div>
                        <div class="d-flex align-items-center flex-wrap mt-2 mt-sm-0">
                            <b-button
                                size="sm"
                                :variant="branchCompareTarget === defaultBranchForPendingDelete ? 'primary' : 'outline-primary'"
                                class="mr-2 mb-1 mb-sm-0 extra-small font-weight-bold"
                                data-test="btn-compare-default-branch"
                                :disabled="loadingBranchCompare && branchCompareTarget === defaultBranchForPendingDelete"
                                @click="comparePendingDeleteBranchAsync(defaultBranchForPendingDelete)"
                            >
                                <i class="icon-azure-branch mr-1"></i>Compare to {{ defaultBranchForPendingDelete }} (default)
                            </b-button>
                            <b-button
                                size="sm"
                                :variant="branchCompareTarget === compareBranchForPendingDelete ? 'info' : 'outline-info'"
                                class="mb-1 mb-sm-0 extra-small font-weight-bold"
                                data-test="btn-compare-target-branch"
                                :disabled="loadingBranchCompare && branchCompareTarget === compareBranchForPendingDelete"
                                @click="comparePendingDeleteBranchAsync(compareBranchForPendingDelete)"
                            >
                                <i class="icon-azure-branch mr-1"></i>Compare to {{ compareBranchForPendingDelete }} (compare)
                            </b-button>
                        </div>
                    </div>

                    <div v-if="loadingBranchCompare" class="text-center py-3" data-state="loading" data-test="branch-compare-loading">
                        <load-spinner :text="`Comparing ${branchPendingDelete.name} against ${branchCompareTarget}...`" size="sm" inline variant="primary" />
                    </div>

                    <div v-else-if="branchCompareError" class="text-center py-2 extra-small text-danger" data-test="branch-compare-error">
                        <span>{{ branchCompareError }}</span>
                    </div>

                    <div v-else-if="branchCompareData" class="mt-2 pt-2 border-top" data-test="branch-compare-results">
                        <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
                            <div class="extra-small text-muted d-flex align-items-center flex-wrap">
                                <span class="mr-2">
                                    <strong>{{ branchCompareData.sourceBranch }}</strong> &rarr; <strong>{{ branchCompareData.targetBranch }}</strong>
                                </span>
                                <b-badge variant="secondary" class="mr-2">{{ (branchCompareData.files || []).length }} file(s) changed</b-badge>
                                <a
                                    v-if="branchCompareData.compareWebUrl"
                                    :href="branchCompareData.compareWebUrl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="text-primary font-weight-bold"
                                    data-test="link-ado-branch-compare"
                                >
                                    <i class="bi bi-box-arrow-up-right mr-1"></i>Open in ADO
                                </a>
                            </div>
                            <b-button-group size="sm" data-test="diff-view-mode-toggle">
                                <b-button
                                    :variant="diffViewMode === 'inline' ? 'primary' : 'outline-secondary'"
                                    class="extra-small py-0 px-2"
                                    data-test="btn-diff-view-inline"
                                    @click="diffViewMode = 'inline'"
                                >
                                    <i class="bi bi-distribute-vertical mr-1"></i>Inline
                                </b-button>
                                <b-button
                                    :variant="diffViewMode === 'split' ? 'primary' : 'outline-secondary'"
                                    class="extra-small py-0 px-2"
                                    data-test="btn-diff-view-split"
                                    @click="diffViewMode = 'split'"
                                >
                                    <i class="bi bi-layout-split mr-1"></i>Side-by-Side
                                </b-button>
                            </b-button-group>
                        </div>

                        <div v-if="(branchCompareData.files || []).length > 0" class="branch-compare-accordion" data-test="branch-compare-accordion">
                            <div
                                v-for="(file, fIdx) in branchCompareData.files"
                                :key="file.path || fIdx"
                                class="branch-compare-file-item border rounded mb-2 overflow-hidden"
                                data-test="branch-compare-file-item"
                            >
                                <div
                                    role="button"
                                    tabindex="0"
                                    class="branch-compare-file-header d-flex align-items-center justify-content-between px-2 py-1"
                                    data-test="branch-compare-file-header"
                                    @click="toggleDiffFileAccordion(file.path)"
                                    @keydown.enter.prevent="toggleDiffFileAccordion(file.path)"
                                    @keydown.space.prevent="toggleDiffFileAccordion(file.path)"
                                >
                                    <div class="d-flex align-items-center text-truncate mr-2" style="min-width: 0;">
                                        <i class="bi mr-2 flex-shrink-0" :class="expandedDiffFiles[file.path] ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                                        <b-badge :variant="getFileChangeBadgeVariant(file.changeType)" class="mr-2 extra-small">{{ file.changeType || 'Edit' }}</b-badge>
                                        <code class="extra-small text-truncate text-body font-weight-bold" :title="file.path">{{ file.path }}</code>
                                    </div>
                                    <div class="d-flex align-items-center flex-shrink-0 extra-small font-weight-bold">
                                        <span class="text-success mr-2">+{{ file.additions || 0 }}</span>
                                        <span class="text-danger">-{{ file.deletions || 0 }}</span>
                                    </div>
                                </div>

                                <div v-if="expandedDiffFiles[file.path]" class="branch-compare-file-diff border-top" data-test="branch-compare-file-diff">
                                    <div v-if="file.isBinary" class="p-2 extra-small text-muted text-center">
                                        Binary file modified — diff preview not available.
                                    </div>
                                    <!-- Inline Diff View -->
                                    <div v-else-if="diffViewMode === 'inline'" class="diff-table-wrap" data-test="diff-view-inline">
                                        <table class="w-100 diff-code-table">
                                            <tbody>
                                                <tr
                                                    v-for="(line, lIdx) in (file.lines || [])"
                                                    :key="lIdx"
                                                    :class="`diff-line diff-line--${line.type}`"
                                                >
                                                    <td class="diff-line-num">{{ line.oldLineNumber || '' }}</td>
                                                    <td class="diff-line-num">{{ line.newLineNumber || '' }}</td>
                                                    <td class="diff-line-prefix">{{ line.type === 'add' ? '+' : (line.type === 'del' ? '-' : ' ') }}</td>
                                                    <td class="diff-line-code"><pre class="mb-0">{{ line.content }}</pre></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <!-- Side-by-Side (Split) Diff View -->
                                    <div v-else class="diff-table-wrap" data-test="diff-view-split">
                                        <table class="w-100 diff-code-table diff-code-table--split">
                                            <tbody>
                                                <tr
                                                    v-for="(pair, pIdx) in getSplitDiffRows(file.lines || [])"
                                                    :key="pIdx"
                                                >
                                                    <td class="diff-line-num" :class="pair.left ? `diff-line--${pair.left.type}` : 'diff-line--empty'">{{ pair.left ? (pair.left.oldLineNumber || '') : '' }}</td>
                                                    <td class="diff-line-code diff-split-cell" :class="pair.left ? `diff-line--${pair.left.type}` : 'diff-line--empty'"><pre class="mb-0">{{ pair.left ? pair.left.content : '' }}</pre></td>
                                                    <td class="diff-line-num border-left" :class="pair.right ? `diff-line--${pair.right.type}` : 'diff-line--empty'">{{ pair.right ? (pair.right.newLineNumber || '') : '' }}</td>
                                                    <td class="diff-line-code diff-split-cell" :class="pair.right ? `diff-line--${pair.right.type}` : 'diff-line--empty'"><pre class="mb-0">{{ pair.right ? pair.right.content : '' }}</pre></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center py-2 extra-small text-muted">
                            No file differences detected between <strong>{{ branchCompareData.sourceBranch }}</strong> and <strong>{{ branchCompareData.targetBranch }}</strong>.
                        </div>
                    </div>
                </div>

                <p class="text-muted small mb-3">
                    Are you absolutely sure you want to delete <strong>{{ branchPendingDelete.name }}</strong> from the remote repository?
                </p>

                <div class="p-2 mb-3 rounded border border-danger" data-test="box-confirm-delete-irreversible">
                    <b-form-checkbox
                        id="chk-confirm-delete-irreversible"
                        v-model="confirmDeleteIrreversible"
                        data-test="chk-confirm-delete-irreversible"
                        class="font-weight-bold text-danger small"
                    >
                        <span data-test="lbl-confirm-delete-irreversible">I understand that this action is irreversible</span>
                    </b-form-checkbox>
                </div>

                <div class="d-flex justify-content-end align-items-center pt-3 border-top">
                    <b-button
                        variant="outline-secondary"
                        size="sm"
                        class="mr-2 px-3"
                        @click="showDeleteBranchModal = false"
                        :disabled="isDeleting"
                        data-delete-btn="true"
                        data-test="btn-cancel-delete-branch"
                    >
                        Cancel
                    </b-button>
                    <b-button
                        variant="danger"
                        size="sm"
                        class="px-3 shadow-sm font-weight-bold"
                        @click="executeDeleteBranch"
                        :disabled="isDeleting || !confirmDeleteIrreversible"
                        data-delete-btn="true"
                        data-test="btn-confirm-delete-branch"
                        :data-state="isDeleting ? 'loading' : 'idle'"
                    >
                        <b-spinner v-if="isDeleting" small class="mr-1"></b-spinner>
                        <i v-else class="bi bi-trash-fill mr-1"></i> Delete Branch Forever
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- Branch Ahead / Behind Commits Inspection Modal -->
        <b-modal
            id="modal-branch-commits"
            v-model="showBranchCommitsModal"
            size="lg"
            dialog-class="modal-dialog-elevated"
            scrollable
            centered
            hide-footer
            data-test="modal-branch-commits"
        >
            <template #modal-title>
                <span class="d-inline-flex align-items-center">
                    <i class="icon-azure-branch text-primary mr-2"></i>
                    <span>Branch Ahead / Behind Commits</span>
                </span>
            </template>

            <div v-if="branchForCommitsModal" class="p-1">
                <!-- Branch Metadata Summary Card -->
                <div class="delete-branch-details p-3 rounded mb-3 border">
                    <div class="d-flex justify-content-between align-items-center flex-wrap py-1">
                        <span class="text-muted small mr-2">Branch:</span>
                        <nav-link-button
                            :href="getBranchAdoUrl(branchForCommitsModal)"
                            :text="branchForCommitsModal.name"
                            icon="icon-azure-branch text-primary"
                            copyable
                            :copy-value="branchForCommitsModal.name"
                            copy-title="Copy branch name"
                            title="Open branch in Azure DevOps"
                            size="sm"
                            class="font-weight-bold"
                            data-test="commits-modal-branch-name"
                        />
                    </div>
                    <div class="d-flex justify-content-between align-items-center flex-wrap py-1">
                        <span class="text-muted small mr-2">Compared Against:</span>
                        <span class="badge badge-secondary px-2 py-1" data-test="commits-modal-base-branch">
                            <i class="icon-azure-branch mr-1"></i>{{ getDefaultBranchNameForRepo(branchForCommitsModal) }} (default)
                        </span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center flex-wrap py-1">
                        <span class="text-muted small mr-2">Repository:</span>
                        <strong class="small font-weight-bold text-body" data-test="commits-modal-repo-name">
                            {{ branchForCommitsModal.repositoryName || 'Active Repository' }}
                        </strong>
                    </div>
                    <div v-if="getBranchAdoUrl(branchForCommitsModal) || getRepoAdoUrl(branchForCommitsModal)" class="d-flex justify-content-between align-items-center flex-wrap py-1">
                        <span class="text-muted small mr-2">Azure DevOps:</span>
                        <a
                            :href="getBranchAdoUrl(branchForCommitsModal) || getRepoAdoUrl(branchForCommitsModal)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="small font-weight-bold text-primary d-inline-flex align-items-center"
                            data-test="link-commits-modal-ado"
                        >
                            <i class="bi bi-box-arrow-up-right mr-1"></i>View Branch in Azure DevOps
                        </a>
                    </div>
                </div>

                <!-- Ahead / Behind Segmented Tabs Card -->
                <div class="ahead-commits-card p-3 rounded mb-3 border">
                    <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
                        <div class="commits-modal-tabs d-inline-flex align-items-center p-1 rounded" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                :aria-selected="commitsModalTab === 'ahead' ? 'true' : 'false'"
                                class="btn btn-sm commits-tab-btn d-inline-flex align-items-center mr-1"
                                :class="{ 'active is-ahead': commitsModalTab === 'ahead' }"
                                data-test="tab-commits-ahead"
                                @click="switchBranchCommitsTab('ahead')"
                            >
                                <i class="bi bi-arrow-up-short mr-1"></i>
                                <span>Ahead</span>
                                <b-badge :variant="commitsModalTab === 'ahead' ? 'success' : 'secondary'" class="ml-2 px-2 py-1">
                                    +{{ branchForCommitsModal.aheadCount || 0 }}
                                </b-badge>
                            </button>
                            <button
                                type="button"
                                role="tab"
                                :aria-selected="commitsModalTab === 'behind' ? 'true' : 'false'"
                                class="btn btn-sm commits-tab-btn d-inline-flex align-items-center"
                                :class="{ 'active is-behind': commitsModalTab === 'behind' }"
                                data-test="tab-commits-behind"
                                @click="switchBranchCommitsTab('behind')"
                            >
                                <i class="bi bi-arrow-down-short mr-1"></i>
                                <span>Behind</span>
                                <b-badge :variant="commitsModalTab === 'behind' ? 'danger' : 'secondary'" class="ml-2 px-2 py-1">
                                    -{{ branchForCommitsModal.behindCount || 0 }}
                                </b-badge>
                            </button>
                        </div>
                        <span class="extra-small text-muted mt-1 mt-sm-0">
                            <template v-if="commitsModalTab === 'ahead'">
                                Unmerged commits unique to <strong>{{ branchForCommitsModal.name }}</strong>
                            </template>
                            <template v-else>
                                Commits in <strong>{{ getDefaultBranchNameForRepo(branchForCommitsModal) }}</strong> missing from this branch
                            </template>
                        </span>
                    </div>

                    <div v-if="loadingModalCommits" class="text-center py-4" data-state="loading" data-test="commits-modal-loading">
                        <load-spinner
                            :text="commitsModalTab === 'ahead' ? 'Retrieving ahead commits...' : 'Retrieving behind commits...'"
                            size="sm"
                            inline
                            variant="primary"
                        />
                    </div>

                    <div v-else-if="modalCommitsError" class="text-center py-3 extra-small text-danger" data-test="commits-modal-error">
                        <span>{{ modalCommitsError }}</span>
                        <b-button size="sm" variant="link" class="p-0 ml-2 extra-small" @click="loadModalCommitsAsync(branchForCommitsModal, commitsModalTab, true)">
                            Retry
                        </b-button>
                    </div>

                    <div v-else-if="activeModalCommits.length > 0" class="ahead-commits-list ahead-commits-list--modal" data-test="commits-modal-list">
                        <div
                            v-for="c in activeModalCommits"
                            :key="`${commitsModalTab}-${c.commitId}`"
                            class="ahead-commit-row d-flex align-items-center justify-content-between py-2 px-2 rounded mb-1"
                            :class="{ 'ahead-commit-row--mine': isCommitMine(c), 'ahead-commit-row--other': !isCommitMine(c) }"
                            :data-author-self="isCommitMine(c) ? 'true' : 'false'"
                            data-test="branch-commit-row"
                        >
                            <div class="d-flex align-items-center text-truncate mr-2" style="min-width: 0;">
                                <nav-link-button
                                    :href="getCommitAdoUrl(c, branchForCommitsModal)"
                                    :text="c.shortCommitId || (c.commitId ? c.commitId.slice(0, 8) : '')"
                                    icon="bi bi-hash"
                                    copyable
                                    :copy-value="c.commitId"
                                    copy-title="Copy full commit SHA"
                                    title="Open commit in Azure DevOps"
                                    size="sm"
                                    class="mr-2 flex-shrink-0"
                                    data-test="link-commit-sha"
                                />
                                <span class="small text-truncate" :class="isCommitMine(c) ? 'font-weight-bold text-body' : 'text-muted'" :title="c.comment">
                                    {{ c.comment || 'No commit message' }}
                                </span>
                            </div>
                            <div class="d-flex align-items-center flex-shrink-0 extra-small text-muted">
                                <template v-if="c.authorName">
                                    <b-badge v-if="isCommitMine(c)" variant="info" class="mr-1 px-1 py-0 extra-small font-weight-bold" data-test="badge-commit-mine">You</b-badge>
                                    <span class="mr-2 d-none d-sm-inline" :class="isCommitMine(c) ? 'font-weight-bold text-info' : 'opacity-75'" :title="c.authorEmail">
                                        <i :class="isCommitMine(c) ? 'bi bi-person-check-fill mr-1' : 'bi bi-person mr-1'"></i>{{ c.authorName }}
                                    </span>
                                </template>
                                <span v-if="c.authorDate" :title="formatDateFull(c.authorDate)">
                                    <i class="bi bi-clock mr-1"></i>{{ formatDateRelative(c.authorDate) }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div v-else class="text-center py-3 small text-muted" data-test="commits-modal-empty">
                        <template v-if="commitsModalTab === 'ahead' && !(branchForCommitsModal.aheadCount > 0)">
                            <i class="bi bi-check-circle-fill text-success mr-2"></i>
                            <span><strong>0 commits ahead:</strong> This branch has no unmerged commits relative to <strong>{{ getDefaultBranchNameForRepo(branchForCommitsModal) }}</strong>.</span>
                        </template>
                        <template v-else-if="commitsModalTab === 'behind' && !(branchForCommitsModal.behindCount > 0)">
                            <i class="bi bi-check-circle-fill text-success mr-2"></i>
                            <span><strong>Up to date:</strong> This branch is 0 commits behind <strong>{{ getDefaultBranchNameForRepo(branchForCommitsModal) }}</strong>.</span>
                        </template>
                        <template v-else>
                            <span>No commit details could be resolved from comparison.</span>
                        </template>
                    </div>
                </div>

                <div class="d-flex justify-content-end align-items-center pt-2 border-top">
                    <b-button
                        variant="secondary"
                        size="sm"
                        class="px-3"
                        data-test="btn-close-branch-commits"
                        @click="showBranchCommitsModal = false"
                    >
                        Close
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { debounce } from 'lodash';
import { formatDistanceToNow, format } from 'date-fns';
import accountMixin from '../mixinAccount';
import toastMixin from '../mixinToast';
import GridShell from './grid-shell.vue';
import CardMissingSource from './card-missing-source.vue';
import PickerSprints from './picker-sprints.vue';
import NavLinkButton from './nav-link-button.vue';
import GridButtonSection from './grid-button-section.vue';
import FilterAuthorDropdown from './filter-author-dropdown.vue';
import FilterMultiSelect from './filter-multi-select.vue';
import GridHeaderBanner from './grid-header-banner.vue';
import eventBus from '../event-bus';
import axios from 'axios';

export default {
    name: 'PageRepoManager',
    mixins: [accountMixin, toastMixin],
    components: {
        'grid-header-banner': GridHeaderBanner,
        'grid-shell': GridShell,
        'card-missing-source': CardMissingSource,
        'picker-sprints': PickerSprints,
        'nav-link-button': NavLinkButton,
        'grid-button-section': GridButtonSection,
        'filter-author-dropdown': FilterAuthorDropdown,
        'filter-multi-select': FilterMultiSelect
    },
    data() {
        return {
            loading: false,
            branchesAbortController: null,
            isDeleting: false,
            confirmDeleteIrreversible: false,
            branchPendingDelete: null,
            showDeleteBranchModal: false,
            aheadCommits: [],
            loadingAheadCommits: false,
            aheadCommitsError: null,
            deleteModalPrs: [],
            loadingDeleteModalPrs: false,
            deleteModalPrsError: null,
            branchCompareData: null,
            branchCompareTarget: null,
            loadingBranchCompare: false,
            branchCompareError: null,
            diffViewMode: 'inline',
            expandedDiffFiles: {},
            showBranchCommitsModal: false,
            branchForCommitsModal: null,
            commitsModalTab: 'ahead',
            modalAheadCommits: [],
            modalBehindCommits: [],
            modalAheadLoaded: false,
            modalBehindLoaded: false,
            loadingModalCommits: false,
            modalCommitsError: null,
            searchTerm: '',
            repoPattern: '',
            selectedRepositoryId: null,
            availableRepositories: [],
            loadingRepositories: false,
            statusFilter: ['active', 'stale', 'merged'],
            statusOptions: [
                { value: 'active', text: 'Active' },
                { value: 'stale', text: 'Stale' },
                { value: 'merged', text: 'Merged' }
            ],
            specialFilter: [],
            specialOptions: [
                { value: 'default', text: 'Default (main, master)' },
                { value: 'develop', text: 'Develop (develop, dev, trunk)' },
                { value: 'release', text: 'Release (release/*)' },
                { value: 'hotfix', text: 'Hotfix (hotfix/*)' }
            ],
            selectedAuthor: 'me',
            groupMode: 'none',
            groupOptions: [
                { value: "none", text: "No Grouping" },
                { value: "repo", text: "By Repository" }
            ],
            collapsesStates: [],
            allTimeFilter: false,
            selectedSprint: null,
            selectedSprintPath: null,
            hasSprints: false,
            initialBranchesLoaded: false,
            branches: [],
            debouncedSearch: null,
            showSprintInfoBanner: false,
            appliedAuthor: 'me',
            appliedStatusFilter: ['Active', 'Completed', 'Abandoned'],
            appliedSpecialFilter: [],
            branchFeatureSettings: this.loadStoredBranchFeatureSettings()
        };
    },
    created() {
        this.loadPersistedFilters(this.activeProject?.id);
        this.appliedAuthor = this.selectedAuthor;
        this.appliedStatusFilter = [...(this.statusFilter || [])];
        this.appliedSpecialFilter = [...(this.specialFilter || [])];

        this.debouncedSearch = debounce(() => {
            // Handled reactively via filteredBranches
        }, 200);

        if (this.hasRepoSource) {
            if (!this.hasBacklogConnection) {
                this.allTimeFilter = true;
                this.initialBranchesLoaded = true;
                this.loadBranchesAsync();
            }
        }

        this.onServiceReconnected = () => {
            if (this.hasRepoSource) {
                this.loadBranchesAsync();
            }
        };
        eventBus.$on('service-reconnected', this.onServiceReconnected);
    },
    beforeUnmount() {
        if (this.branchesAbortController) {
            this.branchesAbortController.abort();
            this.branchesAbortController = null;
        }
        if (this.onServiceReconnected) {
            eventBus.$off('service-reconnected', this.onServiceReconnected);
        }
    },
    watch: {
        globalProjectId: {
            handler() {
                this.initialBranchesLoaded = false;
                this.selectedRepositoryId = null;
                this.loadPersistedFilters(this.activeProject?.id);
                if (this.hasRepoSource) {
                    if (!this.hasBacklogConnection) {
                        this.allTimeFilter = true;
                        this.initialBranchesLoaded = true;
                        this.loadBranchesAsync();
                    }
                } else {
                    this.branches = [];
                    this.availableRepositories = [];
                }
            }
        },
        visibleGroups(groups) {
            if (this.collapsesStates.length !== groups.length) {
                this.collapsesStates = groups.map(() => false);
            }
        }
    },
    computed: {
        branchFeatureConfig() {
            const cfg = this.branchFeatureSettings || {};
            return {
                title: 'Branch Hygiene & Sync Signals',
                subtitle: 'Configure how ahead/behind divergence counters, special integration branch badges, and ownership indicators appear in the Branches grid.',
                icon: 'icon-azure-branch',
                masterLabel: 'Enable Branch Signals',
                masterTestId: 'toggle-branch-features-master',
                storageKey: 'vt_branch_feature_config',
                enabled: cfg.enabled !== false,
                items: [
                    {
                        key: 'showAheadBehindPills',
                        label: 'Interactive Ahead / Behind Divergence Pills',
                        badgeText: '+3 / -1',
                        badgeVariant: 'emerald',
                        badgeIcon: 'bi bi-arrow-left-right',
                        description: 'Show clickable ahead and behind commit counters that open the commit comparison modal.',
                        testId: 'toggle-branch-ahead-behind',
                        enabled: cfg.showAheadBehindPills !== false
                    },
                    {
                        key: 'showSpecialBadges',
                        label: 'Special Integration Branch Badges',
                        badgeText: 'default / release',
                        badgeVariant: 'sky',
                        badgeIcon: 'bi bi-star-fill',
                        description: 'Display badges identifying default, develop, release, and hotfix branches.',
                        testId: 'toggle-branch-special-badges',
                        enabled: cfg.showSpecialBadges !== false
                    },
                    {
                        key: 'showMineBadge',
                        label: 'Personal Ownership ("mine") Badge',
                        badgeText: 'mine',
                        badgeVariant: 'amber',
                        badgeIcon: 'bi bi-person-check-fill',
                        description: 'Highlight branches where you authored the latest commit when viewing team branches.',
                        testId: 'toggle-branch-mine-badge',
                        enabled: cfg.showMineBadge !== false
                    }
                ]
            };
        },
        defaultBranchForPendingDelete() {
            return this.getDefaultBranchNameForRepo(this.branchPendingDelete);
        },
        compareBranchForPendingDelete() {
            const b = this.branchPendingDelete;
            const defaultName = this.defaultBranchForPendingDelete || 'main';
            if (!b) return defaultName.toLowerCase() === 'develop' ? 'main' : 'develop';
            const repoBranches = (this.branches || []).filter(other => other && other.repositoryId === b.repositoryId);
            const explicitCompare = repoBranches.find(other => other.isCompare && other.name && other.name !== defaultName && other.name !== b.name);
            if (explicitCompare) return explicitCompare.name;
            const candidateNames = ['develop', 'dev', 'main', 'master', 'trunk'];
            const found = repoBranches.find(other => {
                const raw = (other.name || '').replace(/^refs\/heads\//i, '').toLowerCase();
                return candidateNames.includes(raw) && other.name !== defaultName && other.name !== b.name;
            });
            if (found) return found.name;
            return defaultName.toLowerCase() === 'develop' ? 'main' : 'develop';
        },
        hasAbandonedDeleteModalPr() {
            return (this.deleteModalPrs || []).some(pr => (pr.status || '').toLowerCase() === 'abandoned');
        },
        activeProject() {
            return this.globalProject;
        },
        activeModalCommits() {
            return this.commitsModalTab === 'behind' ? (this.modalBehindCommits || []) : (this.modalAheadCommits || []);
        },
        hasRepoSource() {
            return !!(this.activeProject && this.activeProject.repoLinkId);
        },
        hasBacklogConnection() {
            return this.currentProjectHasBacklog;
        },
        hasVisibleSprints() {
            return this.hasBacklogConnection && this.hasSprints;
        },
        isMissingRepoVisible() {
            if (!this.isAuthenticated) return false;
            return !this.hasRepoSource;
        },
        isServiceUnavailable() {
            return !!this.$store.state.isServiceUnavailable;
        },
        isStatusFilterActive() {
            return this.statusFilter.length !== this.statusOptions.length;
        },
        statusFilterText() {
            const count = this.statusFilter.length;
            if (count === 0) return "None";
            if (count === this.statusOptions.length) return "All Statuses";
            return this.statusOptions
                .filter(o => this.statusFilter.includes(o.value))
                .map(o => o.text)
                .join(", ");
        },
        specialFilterText() {
            if (this.specialFilter.length === 0) return "Excluded";
            if (this.specialFilter.length === this.specialOptions.length) return "All Included";
            return `+${this.specialFilter.length} Included`;
        },
        selectedAuthors() {
            return [this.selectedAuthor];
        },
        isAuthorFilterActive() {
            return this.selectedAuthor !== 'me';
        },
        isRepoFilterActive() {
            return !!(this.repoPattern && this.repoPattern.trim());
        },
        isSearchFilterActive() {
            return !!(this.searchTerm && this.searchTerm.trim());
        },
        hasHiddenSpecialBranches() {
            return this.specialFilter.length < this.specialOptions.length && (this.branches || []).some(b => {
                const type = this.getSpecialBranchType(b);
                return type && !this.specialFilter.includes(type);
            });
        },
        isSpecialFilterActive() {
            return this.specialFilter.length > 0;
        },
        hasUnappliedFilters() {
            if (this.selectedAuthor !== this.appliedAuthor) return true;
            if (!this.areArraysEqual(this.statusFilter, this.appliedStatusFilter)) return true;
            if (!this.areArraysEqual(this.specialFilter, this.appliedSpecialFilter)) return true;
            return false;
        },
        isAnyFilterActive() {
            return this.isStatusFilterActive
                || this.isRepoFilterActive
                || this.isSearchFilterActive
                || this.isSpecialFilterActive
                || this.isAuthorFilterActive;
        },
        groupModeLabel() {
            const opt = this.groupOptions.find(o => o.value === this.groupMode);
            return opt ? opt.text : "No Grouping";
        },
        isGroupedByRepo() {
            return this.groupMode === 'repo';
        },
        isAllCollapsesOpen() {
            return this.collapsesStates.length > 0 && this.collapsesStates.every(s => !s);
        },
        gridColumns() {
            const isGrouped = this.groupMode !== 'none';
            const cols = [];

            if (isGrouped) {
                cols.push({ key: "collapser", label: "", width: "2.5rem", headerClass: "text-center" });
            }

            if (!this.isGroupedByRepo) {
                // If no grouping is used (or grouped by project), give same size for repo and branch columns!
                const colWidth = "minmax(8.125rem, 1fr)";
                cols.push({ key: "repository", label: "Repository", width: colWidth, headerClass: "text-left" });
                cols.push({ key: "name", label: "Branch", width: colWidth, headerClass: "text-left" });
            } else {
                cols.push({ key: "name", label: "Branch", width: "minmax(9.375rem, 2fr)", headerClass: "text-left" });
            }

            cols.push({ key: "compare", label: "Behind / Ahead", width: "8.75rem", headerClass: "text-center" });
            cols.push({ key: "status", label: "Status", width: "7.5rem", headerClass: "text-center" });
            cols.push({ key: "commit", label: "Last Commit", width: "12.5rem", headerClass: "text-left" });
            cols.push({ key: "author", label: "Author", width: "10.625rem", headerClass: "text-left" });
            cols.push({ key: "action", label: "Action", width: "6.5rem", headerClass: "text-center" });

            return cols;
        },
        branchStats() {
            const list = this.filteredBranches || [];
            const unmergedCount = list.filter(b => (b.aheadCount > 0) && !b.hasPullRequest).length;
            const behindCount = list.filter(b => b.behindCount > 0).length;
            const activeCount = list.filter(b => b.status === 'Active').length;
            const totalCount = this.allBranchesCount;

            const stats = [];

            if (unmergedCount > 0) {
                stats.push({
                    key: 'unmerged',
                    label: 'Unmerged (No PR)',
                    count: unmergedCount,
                    icon: 'bi bi-exclamation-triangle-fill',
                    variant: 'rose',
                    testId: 'stat-branches-unmerged',
                    tooltip: 'Branches with unmerged commits ahead of default branch and no active PR'
                });
            }

            if (behindCount > 0) {
                stats.push({
                    key: 'behind',
                    label: 'Behind Main',
                    count: behindCount,
                    icon: 'bi bi-arrow-down-circle',
                    variant: 'amber',
                    testId: 'stat-branches-behind',
                    tooltip: 'Branches that need fast-forwarding or rebasing from default branch'
                });
            }

            stats.push({
                key: 'active',
                label: 'Active',
                count: activeCount,
                icon: 'icon-azure-branch',
                variant: 'sky',
                testId: 'stat-branches-active',
                showZero: true,
                tooltip: 'Active feature and topic branches'
            });

            stats.push({
                key: 'total',
                label: 'Total Branches',
                count: totalCount,
                icon: 'bi bi-collection',
                variant: 'slate',
                testId: 'stat-branches-total',
                showZero: true,
                tooltip: 'All remote branches matching current repository scope'
            });

            return stats;
        },
        myBranchesCount() {
            return this.filteredBranches.filter(b => this.isBranchMine(b)).length;
        },
        allBranchesCount() {
            return this.filteredBranches.length;
        },
        branchEmptyTitle() {
            if (this.isAnyFilterActive) {
                return 'No branches match your filters';
            }
            return 'No branches found';
        },
        branchEmptyMessage() {
            if (this.isAnyFilterActive) {
                return 'No branches match the current combination of repository patterns, search terms, authors, statuses, or sprint window.';
            }
            return 'There are no branches in the selected repository scope.';
        },
        branchFilterSuggestions() {
            const suggestions = [];
            if (this.repoPattern && this.repoPattern.trim()) {
                suggestions.push({
                    text: `Clear or broaden repository pattern "${this.repoPattern.trim()}".`,
                    action: 'clear-repo',
                    actionLabel: 'Clear Repo Pattern'
                });
            }
            if (this.searchTerm && this.searchTerm.trim()) {
                suggestions.push({
                    text: `Clear or broaden branch search pattern "${this.searchTerm.trim()}".`,
                    action: 'clear-search',
                    actionLabel: 'Clear Search'
                });
            }
            if (this.selectedAuthor === 'me') {
                suggestions.push({
                    text: "Switch author filter from 'Me' to your team to view all team contributions.",
                    action: 'author-team',
                    actionLabel: 'View Team'
                });
            }
            if (this.specialFilter.length < this.specialOptions.length) {
                suggestions.push({
                    text: 'Include special integration branches (main, develop, release, hotfix).',
                    action: 'include-special',
                    actionLabel: 'Include Special Branches'
                });
            }
            if (!this.allTimeFilter) {
                suggestions.push({
                    text: "Select 'All Time' or pick a different sprint to check for branches outside the active sprint date window.",
                    action: 'all-time',
                    actionLabel: 'Show All Time'
                });
            }
            if (this.statusFilter.length < this.statusOptions.length) {
                suggestions.push({
                    text: 'Enable all branch statuses (Active, Stale, Merged) to uncover merged or dormant branches.',
                    action: 'all-statuses',
                    actionLabel: 'All Statuses'
                });
            }
            return suggestions;
        },
        branchEmptyActions() {
            const actions = [];
            if (this.specialFilter.length < this.specialOptions.length) {
                actions.push({
                    text: 'Include Special Branches',
                    icon: 'bi bi-star-fill',
                    action: 'include-special',
                    variant: 'outline-primary'
                });
            }
            if (!this.allTimeFilter) {
                actions.push({
                    text: 'Show All Time',
                    icon: 'bi bi-clock-history',
                    action: 'all-time',
                    variant: 'outline-secondary'
                });
            }
            if (this.selectedAuthor === 'me') {
                actions.push({
                    text: 'View Team Branches',
                    icon: 'bi bi-people',
                    action: 'author-team',
                    variant: 'outline-secondary'
                });
            }
            return actions;
        },
        repositories() {
            const map = new Map();
            (this.availableRepositories || []).forEach(r => {
                if (r.id && !map.has(r.id)) {
                    map.set(r.id, {
                        id: r.id,
                        name: r.name,
                        webUrl: r.webUrl,
                        projectName: r.projectName
                    });
                }
            });
            (this.branches || []).forEach(b => {
                if (b.repositoryId && !map.has(b.repositoryId)) {
                    map.set(b.repositoryId, {
                        id: b.repositoryId,
                        name: b.repositoryName,
                        webUrl: b.repositoryWebUrl,
                        projectName: b.projectName
                    });
                }
            });
            return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        },
        primaryRepoAdoBranchesUrl() {
            const firstBranch = (this.filteredBranches && this.filteredBranches[0]) || (this.branches && this.branches[0]);
            if (firstBranch) {
                return this.getRepoAdoUrl(firstBranch);
            }
            const firstRepo = this.repositories && this.repositories[0];
            if (firstRepo) {
                return this.getRepoAdoUrl(firstRepo);
            }
            return null;
        },
        filteredBranches() {
            return this.branches || [];
        },
        visibleGroups() {
            const filtered = this.filteredBranches;

            if (this.groupMode === "none") {
                const flat = {
                    groupId: "__all__",
                    groupName: "",
                    icon: "bi bi-git",
                    isFlat: true,
                    branches: filtered
                };
                return flat.branches.length ? [flat] : [];
            }

            const groups = {};
            filtered.forEach(b => {
                const id = b.repositoryId || b.repositoryName || 'unknown-repo';
                const name = b.repositoryName || 'Unknown Repository';
                if (!groups[id]) {
                    groups[id] = {
                        groupId: id,
                        groupName: name,
                        icon: 'bi bi-git',
                        branches: []
                    };
                }
                groups[id].branches.push(b);
            });

            return Object.values(groups).sort((a, b) => a.groupName.localeCompare(b.groupName));
        },
        sprintScopeDescription() {
            if (this.allTimeFilter) {
                return 'all branches shown';
            }
            if (this.selectedSprint) {
                const name = this.selectedSprint.name || this.selectedSprintPath || 'active sprint';
                if (this.selectedSprint.from && this.selectedSprint.to) {
                    try {
                        const fromD = new Date(this.selectedSprint.from);
                        const toD = new Date(this.selectedSprint.to);
                        if (!isNaN(fromD.getTime()) && !isNaN(toD.getTime())) {
                            return `${name} · ${format(fromD, 'MMM d')} – ${format(toD, 'MMM d, yyyy')}`;
                        }
                    } catch (e) {
                        // ignore
                    }
                }
                return name;
            }
            if (this.selectedSprintPath) {
                return this.selectedSprintPath;
            }
            return 'active sprint window';
        }
    },
    methods: {
        loadStoredBranchFeatureSettings() {
            const defaults = {
                enabled: true,
                showAheadBehindPills: true,
                showSpecialBadges: true,
                showMineBadge: true
            };
            try {
                if (typeof localStorage !== 'undefined') {
                    const raw = localStorage.getItem('vt_branch_feature_config');
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        if (parsed && typeof parsed === 'object') {
                            return {
                                enabled: parsed.enabled !== false,
                                showAheadBehindPills: parsed.showAheadBehindPills !== false,
                                showSpecialBadges: parsed.showSpecialBadges !== false,
                                showMineBadge: parsed.showMineBadge !== false
                            };
                        }
                    }
                }
            } catch (e) {
                // ignore
            }
            return defaults;
        },
        onBranchFeatureSettingsSaved(newSettings) {
            if (!newSettings || typeof newSettings !== 'object') return;
            this.branchFeatureSettings = {
                enabled: newSettings.enabled !== false,
                showAheadBehindPills: newSettings.showAheadBehindPills !== false,
                showSpecialBadges: newSettings.showSpecialBadges !== false,
                showMineBadge: newSettings.showMineBadge !== false
            };
        },
        getRepoFilterStorageKey(projectId) {
            return projectId ? `vt_repo_filter_pattern_${projectId}` : 'vt_repo_filter_pattern';
        },
        getSearchFilterStorageKey(projectId) {
            return projectId ? `vt_branch_filter_pattern_${projectId}` : 'vt_branch_filter_pattern';
        },
        getSpecialFilterStorageKey(projectId) {
            return projectId ? `vt_special_filter_v2_${projectId}` : 'vt_special_filter_v2';
        },
        loadPersistedFilters(projectId) {
            try {
                const repoKey = this.getRepoFilterStorageKey(projectId);
                const savedRepo = localStorage.getItem(repoKey) || (projectId ? localStorage.getItem('vt_repo_filter_pattern') : null);
                if (savedRepo !== null) {
                    this.repoPattern = savedRepo;
                }
                const searchKey = this.getSearchFilterStorageKey(projectId);
                const savedSearch = localStorage.getItem(searchKey) || (projectId ? localStorage.getItem('vt_branch_filter_pattern') : null);
                if (savedSearch !== null) {
                    this.searchTerm = savedSearch;
                }
                const specialKey = this.getSpecialFilterStorageKey(projectId);
                const savedSpecial = localStorage.getItem(specialKey) || (projectId ? localStorage.getItem('vt_special_filter_v2') : null);
                if (savedSpecial !== null) {
                    try {
                        this.specialFilter = JSON.parse(savedSpecial);
                    } catch (e) {
                        this.specialFilter = savedSpecial ? savedSpecial.split(',') : [];
                    }
                }
            } catch (e) {
                // Ignore localStorage errors
            }
        },
        savePersistedFilters(projectId) {
            try {
                const repoKey = this.getRepoFilterStorageKey(projectId);
                if (this.repoPattern && this.repoPattern.trim()) {
                    localStorage.setItem(repoKey, this.repoPattern.trim());
                    localStorage.setItem('vt_repo_filter_pattern', this.repoPattern.trim());
                } else {
                    localStorage.removeItem(repoKey);
                    localStorage.removeItem('vt_repo_filter_pattern');
                }
                const searchKey = this.getSearchFilterStorageKey(projectId);
                if (this.searchTerm && this.searchTerm.trim()) {
                    localStorage.setItem(searchKey, this.searchTerm.trim());
                    localStorage.setItem('vt_branch_filter_pattern', this.searchTerm.trim());
                } else {
                    localStorage.removeItem(searchKey);
                    localStorage.removeItem('vt_branch_filter_pattern');
                }
                const specialKey = this.getSpecialFilterStorageKey(projectId);
                const specialJson = JSON.stringify(this.specialFilter || []);
                localStorage.setItem(specialKey, specialJson);
                localStorage.setItem('vt_special_filter_v2', specialJson);
            } catch (e) {
                // Ignore localStorage errors
            }
        },
        clearPersistedFilters(projectId) {
            try {
                localStorage.removeItem(this.getRepoFilterStorageKey(projectId));
                localStorage.removeItem('vt_repo_filter_pattern');
                localStorage.removeItem(this.getSearchFilterStorageKey(projectId));
                localStorage.removeItem('vt_branch_filter_pattern');
                localStorage.removeItem(this.getSpecialFilterStorageKey(projectId));
                localStorage.removeItem('vt_special_filter_v2');
            } catch (e) {
                // Ignore localStorage errors
            }
        },
        clearSearch() {
            this.searchTerm = '';
            this.applyFilters();
        },
        clearRepoPattern() {
            this.repoPattern = '';
            this.applyFilters();
        },
        selectAllStatuses() {
            this.statusFilter = this.statusOptions.map(o => o.value);
        },
        clearAllStatuses() {
            this.statusFilter = [];
        },
        actionHoverPop(text) {
            return {
                content: text,
                placement: 'top',
                trigger: 'hover',
                customClass: 'action-hover-pop'
            };
        },
        toggleCollapseAll() {
            const anyOpen = this.collapsesStates.some(s => !s);
            this.collapsesStates = this.visibleGroups.map(() => anyOpen);
        },
        toggleCollapseSingle(index) {
            this.$set(this.collapsesStates, index, !this.collapsesStates[index]);
        },
        isBranchMine(b) {
            if (!b) return false;
            if (b.isDefault) return false;
            if (b.isMyBranch) return true;

            const author = (b.lastCommitAuthorName || '').toLowerCase();
            const email = (b.lastCommitAuthorEmail || '').toLowerCase();
            const name = (b.name || '').toLowerCase();

            const hasFirst = author.includes('dzhem') || author.includes('jem') || email.includes('jem') || email.includes('dzhem');
            const hasLast = author.includes('hasan') || email.includes('hasan');
            if (hasFirst && hasLast) return true;

            if (email.includes('jemyto') || email.includes('dhasan') || email.includes('jhasan')) return true;
            if (name.includes('users/dzhem') || name.includes('users/jem') || name.includes('users/dhasan') || name.includes('users/jhasan')) return true;

            const myName = (this.user?.fullName || '').toLowerCase().trim();
            const myEmail = (this.user?.email || '').toLowerCase().trim();
            if (myEmail && email === myEmail) return true;
            if (myName && author === myName) return true;

            return false;
        },
        areArraysEqual(a, b) {
            if (!a && !b) return true;
            if (!a || !b) return false;
            if (a.length !== b.length) return false;
            const sortedA = [...a].sort();
            const sortedB = [...b].sort();
            return sortedA.every((v, i) => v === sortedB[i]);
        },
        selectAllSpecial() {
            this.specialFilter = this.specialOptions.map(o => o.value);
        },
        async loadRepositoriesAsync() {
            if (!this.hasRepoSource) {
                this.availableRepositories = [];
                return;
            }
            this.loadingRepositories = true;
            try {
                const resp = await this.$http.get("/Azure/ListRepositories", {
                    params: { projectId: this.activeProject?.id },
                    skipGlobalPermissionToast: true
                });
                this.availableRepositories = resp.data || [];
            } catch (err) {
                console.error("Failed to load repositories", err);
                this.availableRepositories = [];
            } finally {
                this.loadingRepositories = false;
            }
        },
        applyFilters() {
            this.savePersistedFilters(this.activeProject?.id);
            this.loadBranchesAsync();
        },
        resetToDefaults() {
            this.repoPattern = '';
            this.searchTerm = '';
            this.selectedAuthor = 'me';
            this.selectAllStatuses();
            this.specialFilter = [];
            this.clearPersistedFilters(this.activeProject?.id);
            this.applyFilters();
        },
        onEmptyAction(actionKey) {
            if (actionKey === 'clear-repo') {
                this.clearRepoPattern();
            } else if (actionKey === 'clear-search') {
                this.clearSearch();
            } else if (actionKey === 'include-special') {
                this.selectAllSpecial();
                this.applyFilters();
            } else if (actionKey === 'author-team') {
                this.selectedAuthor = 'team';
                this.applyFilters();
            } else if (actionKey === 'all-time') {
                this.allTimeFilter = true;
                this.applyFilters();
            } else if (actionKey === 'all-statuses') {
                this.selectAllStatuses();
                this.applyFilters();
            }
        },
        toggleSprintInfoBanner() {
            this.showSprintInfoBanner = !this.showSprintInfoBanner;
        },
        dismissSprintInfoBanner() {
            this.showSprintInfoBanner = false;
        },
        restoreSprintInfoBanner() {
            this.showSprintInfoBanner = true;
        },
        async onSprintPicked(sprintPath, sprint, project, isAllTime = false) {
            if (isAllTime) {
                this.allTimeFilter = true;
                this.selectedSprint = null;
                this.selectedSprintPath = null;
            } else {
                this.allTimeFilter = false;
                this.selectedSprint = sprint;
                this.selectedSprintPath = sprintPath;
                if (sprintPath) {
                    this.hasSprints = true;
                }
            }
            this.initialBranchesLoaded = true;
            await this.loadBranchesAsync();
        },
        onSprintsLoaded(sprints) {
            this.hasSprints = Array.isArray(sprints) && sprints.length > 0;
            if (!this.hasSprints && !this.initialBranchesLoaded) {
                this.initialBranchesLoaded = true;
                this.allTimeFilter = true;
                this.loadBranchesAsync();
            }
        },
        getRepoAdoUrl(item) {
            if (!item) return null;
            let baseUrl = item.repositoryWebUrl || item.webUrl;
            if (!baseUrl && item.repositoryId) {
                const repo = this.repositories.find(r => r.id === item.repositoryId);
                if (repo?.webUrl) baseUrl = repo.webUrl;
            }
            if (!baseUrl && item.id) {
                const repo = this.repositories.find(r => r.id === item.id);
                if (repo?.webUrl) baseUrl = repo.webUrl;
            }
            if (!baseUrl) {
                const repoName = item.repositoryName || item.name;
                const projectName = item.projectName || this.activeProject?.name || 'core';
                if (repoName) {
                    baseUrl = `https://dev.azure.com/velocytrack/${encodeURIComponent(projectName)}/_git/${encodeURIComponent(repoName)}`;
                }
            }
            if (baseUrl && baseUrl.includes('?')) {
                baseUrl = baseUrl.split('?')[0];
            }
            if (!baseUrl) return null;

            baseUrl = baseUrl.replace(/\/+$/, '');
            if (baseUrl.endsWith('/branches')) {
                return baseUrl;
            }
            return `${baseUrl}/branches`;
        },
        getAdoPatConfigUrl(item) {
            let candidateUrl = item?.repositoryWebUrl || item?.webUrl || '';
            if (!candidateUrl && item?.repositoryId) {
                const repo = (this.repositories || []).find(r => r.id === item.repositoryId);
                if (repo?.webUrl) candidateUrl = repo.webUrl;
            }
            if (!candidateUrl && Array.isArray(this.repositories) && this.repositories.length > 0) {
                const firstRepoWithUrl = this.repositories.find(r => r && r.webUrl);
                if (firstRepoWithUrl?.webUrl) candidateUrl = firstRepoWithUrl.webUrl;
            }
            if (candidateUrl) {
                try {
                    const parsed = new URL(candidateUrl);
                    if (parsed.hostname.toLowerCase() === 'dev.azure.com') {
                        const segs = parsed.pathname.split('/').filter(Boolean);
                        if (segs.length > 0) {
                            return `${parsed.origin}/${encodeURIComponent(decodeURIComponent(segs[0]))}/_usersSettings/tokens`;
                        }
                    } else if (parsed.hostname.toLowerCase().endsWith('.visualstudio.com')) {
                        return `${parsed.origin}/_usersSettings/tokens`;
                    }
                } catch (e) {
                    // Fallback to default ADO PAT settings URL
                }
            }
            return 'https://dev.azure.com/_usersSettings/tokens';
        },
        isCommitMine(c) {
            if (!c) return false;
            const author = (c.authorName || '').toLowerCase().trim();
            const email = (c.authorEmail || '').toLowerCase().trim();

            const hasFirst = author.includes('dzhem') || author.includes('jem') || email.includes('jem') || email.includes('dzhem');
            const hasLast = author.includes('hasan') || email.includes('hasan');
            if (hasFirst && hasLast) return true;
            if (email.includes('jemyto') || email.includes('dhasan') || email.includes('jhasan')) return true;

            const myName = (this.user?.fullName || this.account?.fullName || this.account?.name || '').toLowerCase().trim();
            const myEmail = (this.user?.email || this.account?.email || '').toLowerCase().trim();
            if (myEmail && email && email === myEmail) return true;
            if (myName && author && author === myName) return true;

            const ctxBranch = this.branchPendingDelete || this.branchForCommitsModal;
            if (ctxBranch && this.isBranchMine(ctxBranch)) {
                const branchAuthor = (ctxBranch.lastCommitAuthorName || '').toLowerCase().trim();
                const branchEmail = (ctxBranch.lastCommitAuthorEmail || '').toLowerCase().trim();
                if (branchEmail && email && email === branchEmail) return true;
                if (branchAuthor && author && author === branchAuthor) return true;
            }
            return false;
        },
        getCreatePrAdoUrl(branch) {
            if (!branch || branch.isDefault) return null;
            let repoUrl = branch.repositoryWebUrl;
            if (!repoUrl && branch.repositoryId) {
                const repo = (this.repositories || []).find(r => r.id === branch.repositoryId);
                if (repo?.webUrl) repoUrl = repo.webUrl;
            }
            if (!repoUrl) {
                const repoName = branch.repositoryName;
                const projectName = branch.projectName || this.activeProject?.name || 'core';
                if (repoName) {
                    repoUrl = `https://dev.azure.com/velocytrack/${encodeURIComponent(projectName)}/_git/${encodeURIComponent(repoName)}`;
                }
            }
            if (!repoUrl) return null;
            if (repoUrl.includes('?')) {
                repoUrl = repoUrl.split('?')[0];
            }
            repoUrl = repoUrl.replace(/\/+$/, '').replace(/\/branches$/i, '');
            const sourceBranch = (branch.name || '').replace(/^refs\/heads\//i, '');
            const defaultBranch = (this.getDefaultBranchNameForRepo(branch) || 'main').replace(/^refs\/heads\//i, '');
            if (!sourceBranch) return null;
            return `${repoUrl}/pullrequestcreate?sourceRef=${encodeURIComponent(sourceBranch)}&targetRef=${encodeURIComponent(defaultBranch)}`;
        },
        getPrStatusBadgeVariant(status) {
            const s = (status || '').toLowerCase();
            if (s === 'abandoned') return 'danger';
            if (s === 'completed' || s === 'merged') return 'success';
            if (s === 'active') return 'info';
            return 'secondary';
        },
        getFileChangeBadgeVariant(changeType) {
            const c = (changeType || '').toLowerCase();
            if (c.includes('add')) return 'success';
            if (c.includes('delete')) return 'danger';
            if (c.includes('rename')) return 'warning';
            return 'info';
        },
        async loadBranchPullRequestsAsync(branch) {
            if (!branch || !branch.repositoryId) return;
            this.loadingDeleteModalPrs = true;
            this.deleteModalPrsError = null;
            try {
                const resp = await this.$http.get("/Azure/ListBranchPullRequests", {
                    params: {
                        repositoryId: branch.repositoryId,
                        branchName: branch.name
                    }
                });
                this.deleteModalPrs = resp.data || [];
            } catch (err) {
                console.error("Failed to load branch pull requests", err);
                this.deleteModalPrsError = "Could not retrieve pull request history for this branch.";
                this.deleteModalPrs = [];
            } finally {
                this.loadingDeleteModalPrs = false;
            }
        },
        async comparePendingDeleteBranchAsync(targetBranch) {
            const branch = this.branchPendingDelete;
            if (!branch || !branch.repositoryId || !targetBranch) return;
            this.branchCompareTarget = targetBranch;
            this.loadingBranchCompare = true;
            this.branchCompareError = null;
            try {
                const resp = await this.$http.get("/Azure/CompareBranches", {
                    params: {
                        repositoryId: branch.repositoryId,
                        sourceBranch: branch.name,
                        targetBranch
                    }
                });
                this.branchCompareData = resp.data || null;
                const expanded = {};
                if (this.branchCompareData && Array.isArray(this.branchCompareData.files) && this.branchCompareData.files.length > 0) {
                    expanded[this.branchCompareData.files[0].path] = true;
                }
                this.expandedDiffFiles = expanded;
            } catch (err) {
                console.error("Failed to compare branches", err);
                this.branchCompareError = "Could not load file diff comparison for this branch.";
                this.branchCompareData = null;
            } finally {
                this.loadingBranchCompare = false;
            }
        },
        toggleDiffFileAccordion(filePath) {
            if (!filePath) return;
            this.$set(this.expandedDiffFiles, filePath, !this.expandedDiffFiles[filePath]);
        },
        getSplitDiffRows(lines) {
            if (!Array.isArray(lines) || lines.length === 0) return [];
            const rows = [];
            let i = 0;
            while (i < lines.length) {
                const line = lines[i];
                if (line.type === 'context') {
                    rows.push({ left: line, right: line });
                    i++;
                } else if (line.type === 'del') {
                    const dels = [];
                    while (i < lines.length && lines[i].type === 'del') {
                        dels.push(lines[i]);
                        i++;
                    }
                    const adds = [];
                    while (i < lines.length && lines[i].type === 'add') {
                        adds.push(lines[i]);
                        i++;
                    }
                    const maxLen = Math.max(dels.length, adds.length);
                    for (let k = 0; k < maxLen; k++) {
                        rows.push({ left: dels[k] || null, right: adds[k] || null });
                    }
                } else if (line.type === 'add') {
                    rows.push({ left: null, right: line });
                    i++;
                } else {
                    rows.push({ left: line, right: line });
                    i++;
                }
            }
            return rows;
        },
        _noopAdoPatFallback() {
            return 'https://dev.azure.com/_usersSettings/tokens';
        },
        getSpecialBranchType(b) {
            if (!b) return null;
            if (b.isDefault) return 'default';
            if (b.isCompare) return 'compare';
            const repoHasExplicitDefault = (this.branches || []).some(other => other && other.repositoryId === b.repositoryId && other.isDefault);
            const name = (b.name || '').toLowerCase();
            const rawName = (b.name || '').replace(/^refs\/heads\//i, '').toLowerCase();
            if (rawName === 'main' || rawName === 'master') return repoHasExplicitDefault ? 'compare' : 'default';
            if (rawName === 'develop' || rawName === 'dev' || rawName === 'trunk') return 'develop';
            if (rawName === 'release' || rawName === 'hotfix') return rawName;
            if (name === 'main' || name === 'master') return repoHasExplicitDefault ? 'compare' : 'default';
            if (name === 'develop' || name === 'dev' || name === 'trunk') return 'develop';
            if (rawName.startsWith('release/') || rawName.startsWith('releases/') || rawName.startsWith('rel/')) return 'release';
            if (rawName.startsWith('hotfix/') || rawName.startsWith('hotfixes/')) return 'hotfix';
            return null;
        },
        getSpecialBranchLabel(b) {
            const type = this.getSpecialBranchType(b);
            if (type === 'default') return 'default';
            if (type === 'compare') return 'compare';
            if (type === 'develop') return 'develop';
            if (type === 'release') return 'release';
            if (type === 'hotfix') return 'hotfix';
            return null;
        },
        getSpecialBranchBadgeVariant(type) {
            switch (type) {
                case 'default': return 'primary';
                case 'compare': return 'info';
                case 'develop': return 'dark';
                case 'release': return 'info';
                case 'hotfix': return 'danger';
                default: return 'secondary';
            }
        },
        getBranchAdoUrl(branch) {
            if (!branch) return null;
            if (branch.webUrl) return branch.webUrl;
            const repo = this.repositories.find(r => r.id === branch.repositoryId);
            if (repo?.webUrl && branch.name) {
                return `${repo.webUrl}?version=GB${encodeURIComponent(branch.name)}`;
            }
            const repoName = branch.repositoryName || (repo && repo.name);
            const projectName = branch.projectName || (repo && repo.projectName) || this.activeProject?.name || 'core';
            if (repoName && branch.name) {
                return `https://dev.azure.com/velocytrack/${encodeURIComponent(projectName)}/_git/${encodeURIComponent(repoName)}?version=GB${encodeURIComponent(branch.name)}`;
            }
            return null;
        },
        getCommitAdoUrl(commit, targetBranch = null) {
            if (!commit) return null;
            if (commit.webUrl) return commit.webUrl;
            const branch = targetBranch || this.branchForCommitsModal || this.branchPendingDelete;
            if (branch && commit.commitId) {
                let repoUrl = branch.repositoryWebUrl;
                if (!repoUrl) {
                    const repo = this.repositories.find(r => r.id === branch.repositoryId);
                    if (repo?.webUrl) repoUrl = repo.webUrl;
                }
                if (!repoUrl && branch.repositoryName) {
                    const projectName = branch.projectName || this.activeProject?.name || 'core';
                    repoUrl = `https://dev.azure.com/velocytrack/${encodeURIComponent(projectName)}/_git/${encodeURIComponent(branch.repositoryName)}`;
                }
                if (repoUrl) {
                    return `${repoUrl.replace(/\/+$/, '')}/commit/${encodeURIComponent(commit.commitId)}`;
                }
            }
            return null;
        },
        getDefaultBranchNameForRepo(branch) {
            if (!branch) return 'main';
            const defaultBranch = (this.branches || []).find(b => b.repositoryId === branch.repositoryId && b.isDefault);
            if (defaultBranch && defaultBranch.name) {
                return defaultBranch.name;
            }
            return 'main';
        },
        openBranchCommitsModal(branch, initialTab = 'ahead') {
            if (!branch || branch.isDefault) return;
            this.branchForCommitsModal = branch;
            this.commitsModalTab = initialTab === 'behind' ? 'behind' : 'ahead';
            this.modalAheadCommits = [];
            this.modalBehindCommits = [];
            this.modalAheadLoaded = false;
            this.modalBehindLoaded = false;
            this.modalCommitsError = null;
            this.loadingModalCommits = false;
            this.showBranchCommitsModal = true;
            this.loadModalCommitsAsync(branch, this.commitsModalTab, false);
        },
        switchBranchCommitsTab(tab) {
            const nextTab = tab === 'behind' ? 'behind' : 'ahead';
            if (this.commitsModalTab === nextTab && !this.modalCommitsError) return;
            this.commitsModalTab = nextTab;
            this.modalCommitsError = null;
            if (this.branchForCommitsModal) {
                this.loadModalCommitsAsync(this.branchForCommitsModal, nextTab, false);
            }
        },
        async loadModalCommitsAsync(branch, mode = 'ahead', force = false) {
            if (!branch || !branch.repositoryId) return;
            const isBehind = mode === 'behind';
            if (!force) {
                if (isBehind && this.modalBehindLoaded) return;
                if (!isBehind && this.modalAheadLoaded) return;
            }

            const expectedCount = isBehind ? (branch.behindCount || 0) : (branch.aheadCount || 0);
            if (expectedCount <= 0) {
                if (isBehind) {
                    this.modalBehindCommits = [];
                    this.modalBehindLoaded = true;
                } else {
                    this.modalAheadCommits = [];
                    this.modalAheadLoaded = true;
                }
                return;
            }

            this.loadingModalCommits = true;
            this.modalCommitsError = null;
            try {
                const defaultBranch = (this.branches || []).find(b => b.repositoryId === branch.repositoryId && b.isDefault);
                const limit = Math.min(Math.max(expectedCount, 1), 50);
                const resp = await this.$http.get("/Azure/ListAheadCommits", {
                    params: {
                        repositoryId: branch.repositoryId,
                        branchName: branch.name,
                        defaultBranchName: defaultBranch ? defaultBranch.name : undefined,
                        top: limit,
                        mode: isBehind ? 'behind' : 'ahead'
                    }
                });
                let commits = resp.data || [];
                if (expectedCount > 0 && commits.length > expectedCount) {
                    commits = commits.slice(0, expectedCount);
                }
                if (isBehind) {
                    this.modalBehindCommits = commits;
                    this.modalBehindLoaded = true;
                } else {
                    this.modalAheadCommits = commits;
                    this.modalAheadLoaded = true;
                }
            } catch (err) {
                console.error(`Failed to load ${mode} commits`, err);
                this.modalCommitsError = `Could not retrieve ${isBehind ? 'behind' : 'ahead'} commits from repository.`;
            } finally {
                this.loadingModalCommits = false;
            }
        },
        formatDateRelative(dateStr) {
            if (!dateStr) return '';
            try {
                return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
            } catch (e) {
                return dateStr;
            }
        },
        formatDateFull(dateStr) {
            if (!dateStr) return '';
            try {
                return format(new Date(dateStr), 'PPpp');
            } catch (e) {
                return dateStr;
            }
        },
        async loadBranchesAsync() {
            if (!this.hasRepoSource) {
                this.loading = false;
                return;
            }
            if (this.branchesAbortController) {
                this.branchesAbortController.abort();
                this.branchesAbortController = null;
            }
            const controller = new AbortController();
            this.branchesAbortController = controller;

            this.loading = true;
            this.appliedAuthor = this.selectedAuthor;
            this.appliedStatusFilter = [...(this.statusFilter || [])];
            this.appliedSpecialFilter = [...(this.specialFilter || [])];
            const params = {};
            if (this.activeProject?.id) {
                params.projectId = this.activeProject.id;
            }
            if (this.repoPattern && this.repoPattern.trim()) {
                params.repoPattern = this.repoPattern.trim();
            }
            if (this.searchTerm && this.searchTerm.trim()) {
                params.searchTerm = this.searchTerm.trim();
            }
            if (this.selectedAuthor) {
                params.authors = Array.isArray(this.selectedAuthor) ? this.selectedAuthor.join(',') : this.selectedAuthor;
            }
            if (this.statusFilter && this.statusFilter.length > 0) {
                params.statuses = this.statusFilter.join(',');
            } else {
                params.statuses = 'none';
            }
            if (this.specialFilter && this.specialFilter.length > 0) {
                params.specials = this.specialFilter.join(',');
            } else {
                params.specials = 'none';
            }

            if (this.allTimeFilter) {
                params.allTime = true;
            } else {
                let sprintObj = this.selectedSprint;
                if (!sprintObj && this.selectedSprintPath && Array.isArray(this.$store.state.sprints)) {
                    sprintObj = this.$store.state.sprints.find(s => s.path === this.selectedSprintPath) || null;
                }
                if (!sprintObj && Array.isArray(this.$store.state.sprints)) {
                    sprintObj = this.$store.state.sprints.find(s => s.position === 0) || null;
                }

                const sprintPath = this.selectedSprintPath || sprintObj?.path;
                if (sprintPath) {
                    params.sprint = sprintPath;
                }

                if (sprintObj) {
                    if (sprintObj.from) {
                        const fromDate = new Date(sprintObj.from);
                        if (!isNaN(fromDate.getTime()) && fromDate.getFullYear() >= 2000) {
                            params.sprintFrom = fromDate.toISOString();
                        }
                    }
                    if (sprintObj.to) {
                        const toDate = new Date(sprintObj.to);
                        if (!isNaN(toDate.getTime()) && toDate.getFullYear() >= 2000) {
                            params.sprintTo = toDate.toISOString();
                        }
                    }
                }
            }

            try {
                const resp = await this.$http.get("/Azure/ListBranches", {
                    params,
                    signal: controller.signal
                });
                this.branches = resp.data || [];
            } catch (err) {
                if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
                    return;
                }
                if (err?.isPlanUpgrade) return;
                if (err?.isNetworkError || (!err?.response?.status && !err?.status)) {
                    this.branches = [];
                    return;
                }
                console.error("Failed to load branches", err);
                this.toastError("Failed to load branches");
                this.branches = [];
            } finally {
                if (this.branchesAbortController === controller) {
                    this.branchesAbortController = null;
                    this.loading = false;
                } else if (!controller.signal.aborted) {
                    this.loading = false;
                }
            }
        },
        canDeleteBranch(b) {
            return !b.isDefault && !this.getSpecialBranchType(b);
        },
        branchActionButtons(b) {
            const createPrUrl = this.getCreatePrAdoUrl(b);
            const defaultBranch = this.getDefaultBranchNameForRepo(b) || 'main';
            return [
                {
                    key: 'create-pr',
                    icon: 'icon-azure-pr',
                    variant: 'outline-info',
                    title: `Create Pull Request from '${b.name}' into '${defaultBranch}' in Azure DevOps`,
                    ariaLabel: 'Create Pull Request in Azure DevOps',
                    dataTest: 'btn-create-pr-branch',
                    href: createPrUrl,
                    visible: !b.isDefault && !!createPrUrl
                },
                {
                    key: 'delete',
                    icon: 'bi bi-trash',
                    variant: 'danger',
                    title: this.isViewOnlyMode ? 'Deleting branches requires the Pro Plan' : 'Delete Branch',
                    ariaLabel: 'Delete Branch',
                    dataTest: 'btn-delete-branch',
                    visible: this.canDeleteBranch(b),
                    disabled: this.isDeleting || this.isViewOnlyMode
                }
            ];
        },
        _legacyBranchActionButtons(b) {
            return [
                {
                    key: 'delete',
                    icon: 'bi bi-trash',
                    variant: 'danger',
                    title: this.isViewOnlyMode ? 'Deleting branches requires the Pro Plan' : 'Delete Branch',
                    ariaLabel: 'Delete Branch',
                    dataTest: 'btn-delete-branch',
                    visible: this.canDeleteBranch(b),
                    disabled: this.isDeleting || this.isViewOnlyMode
                }
            ];
        },
        onBranchAction(b, key) {
            if (key === 'delete') {
                this.confirmDeleteBranch(b);
            }
        },
        confirmDeleteBranch(branch) {
            if (this.isViewOnlyMode) {
                this.toastUpgradeRequired('Pro Plan', 'Deleting branches requires the Pro Plan.');
                return;
            }
            if (!branch || !this.canDeleteBranch(branch)) {
                this.toastWarning('Protected Branch', 'Special branches (main, master, develop, release, hotfix) are protected and cannot be deleted.');
                return;
            }
            this.confirmDeleteIrreversible = false;
            this.branchPendingDelete = branch;
            this.showDeleteBranchModal = true;
            this.deleteModalPrs = [];
            this.loadingDeleteModalPrs = false;
            this.deleteModalPrsError = null;
            this.branchCompareData = null;
            this.branchCompareTarget = null;
            this.loadingBranchCompare = false;
            this.branchCompareError = null;
            this.expandedDiffFiles = {};
            if (branch && branch.repositoryId) {
                this.loadBranchPullRequestsAsync(branch);
            }
            this.aheadCommits = [];
            this.aheadCommitsError = null;
            this.loadingAheadCommits = false;
            if (branch && branch.repositoryId && branch.aheadCount > 0) {
                this.loadAheadCommitsAsync(branch);
            }
        },
        async loadAheadCommitsAsync(branch) {
            if (!branch || !branch.repositoryId) return;
            this.loadingAheadCommits = true;
            this.aheadCommitsError = null;
            try {
                // Find default branch for this repository if available in loaded branches list
                const defaultBranch = this.branches.find(b => b.repositoryId === branch.repositoryId && b.isDefault);
                const limit = (branch.aheadCount && branch.aheadCount > 0) ? branch.aheadCount : 25;
                const resp = await this.$http.get("/Azure/ListAheadCommits", {
                    params: {
                        repositoryId: branch.repositoryId,
                        branchName: branch.name,
                        defaultBranchName: defaultBranch ? defaultBranch.name : undefined,
                        top: limit
                    }
                });
                let commits = resp.data || [];
                if (branch.aheadCount > 0 && commits.length > branch.aheadCount) {
                    commits = commits.slice(0, branch.aheadCount);
                }
                this.aheadCommits = commits;
            } catch (err) {
                console.error("Failed to load ahead commits", err);
                this.aheadCommitsError = "Could not retrieve ahead commits from repository.";
                this.aheadCommits = [];
            } finally {
                this.loadingAheadCommits = false;
            }
        },
        async executeDeleteBranch() {
            if (!this.branchPendingDelete || !this.canDeleteBranch(this.branchPendingDelete) || !this.confirmDeleteIrreversible) return;
            const branch = this.branchPendingDelete;
            try {
                await this.deleteBranchAsync(branch);
                this.showDeleteBranchModal = false;
                this.branchPendingDelete = null;
                this.aheadCommits = [];
            } catch (e) {
                // deleteBranchAsync handles errors and toasts
            }
        },
        async deleteBranchAsync(branch) {
            this.isDeleting = true;
            try {
                const payload = {
                    repositoryId: branch.repositoryId,
                    projectId: this.activeProject?.id,
                    branchName: branch.name,
                    objectId: branch.objectId
                };
                await this.$http.post("/Azure/DeleteBranch", payload, {
                    params: { projectId: this.activeProject?.id },
                    skipGlobalPermissionToast: true
                });
                this.toastSuccess("Branch Deleted", `Branch '${branch.name}' has been deleted.`);
                const idx = this.branches.findIndex(b => b.name === branch.name && b.repositoryId === branch.repositoryId);
                if (idx !== -1) {
                    this.branches.splice(idx, 1);
                }
            } catch (err) {
                if (err?.isPlanUpgrade) return;
                const rawErrMsg = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null) || err?.message || "Failed to delete branch.";
                const lowerMsg = (rawErrMsg || '').toLowerCase();
                if (err?.response?.data?.isPermissionError || err?.response?.status === 403 || lowerMsg.includes('write permission') || lowerMsg.includes('contribute') || lowerMsg.includes('force push') || lowerMsg.includes('vs30063') || lowerMsg.includes('tf401027')) {
                    const permData = (err?.response?.data && typeof err.response.data === 'object') ? err.response.data : {};
                    const isForcePushRequired = lowerMsg.includes('forcepushrequired') || lowerMsg.includes('force push') || (permData.requiredPermission || '').toLowerCase().includes('force push');
                    this.toastPermissionRequired({
                        ...permData,
                        actionName: permData.actionName || 'Delete Branch',
                        requiredScope: permData.requiredScope || (isForcePushRequired ? 'Code (Full) / Repository Force Push Security' : 'Code (Read & write)'),
                        requiredPermission: permData.requiredPermission || (isForcePushRequired ? 'Force push (rewrite history, delete branches and tags)' : 'Contribute / Write (delete branch)'),
                        explanation: permData.explanation || (isForcePushRequired
                            ? "Your PAT already has 'Code (Read & Write)' scope (which allows deleting your own branches), but Azure DevOps returned 'ForcePushRequired' for this branch. Deleting branches created by other authors or protected refs requires the repository-level 'Force push' security permission (and 'Code (Full)' scope if your PAT is restrictively scoped)."
                            : "Deleting remote branches requires the 'Code (Read & write)' scope on your Personal Access Token and repository Contribute permission."),
                        guidance: permData.guidance || (isForcePushRequired
                            ? "1. In Azure DevOps: Project Settings → Repositories → select the repository → Security → set 'Force push (rewrite history, delete branches and tags)' to Allow for your user/group.\n2. Open your ADO PAT Configuration (User Settings → Personal Access Tokens) to verify 'Code (Read & Write)' or 'Code (Full)' is enabled."
                            : "1. Open your ADO PAT Configuration (User Settings → Personal Access Tokens) → Edit your token → Enable 'Code (Read & write)'.\n2. Update your connection token in VelocyTrack Configuration."),
                        externalUrl: this.getAdoPatConfigUrl(branch),
                        externalLabel: 'Open ADO PAT Configuration',
                        configLabel: 'Update Token in Config'
                    });
                    throw err;
                }
                console.error("Failed to delete branch", err);
                const errMsg = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null) || err?.message || "Failed to delete branch.";
                this.toastError("Delete Branch Failed", errMsg);
            } finally {
                this.isDeleting = false;
            }
        }
    }
};
</script>

<style scoped>
.repo-manager-page {
    min-height: calc(100vh - 120px);
}
.repo-branches-grid .grid-row {
    align-items: center;
    min-width: 0 !important;
}
.repo-branches-grid .repo-col-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
}
.repo-branches-grid ::v-deep .grid-shell-scroll {
    min-height: 280px;
}
.repo-grid-overlay {
    min-height: 280px;
    width: 100%;
    display: block;
}
.repo-grid-overlay.no-sprints {
    margin-top: 1.25rem;
}
.filter-toolbar-divider {
    width: 1px;
    height: 24px;
    background-color: rgba(128, 128, 128, 0.25);
    align-self: center;
}
.row-expand-icon {
    display: inline-block;
    transition: transform 0.2s ease-in-out;
}
.row-expand-icon.is-expanded {
    transform: rotate(90deg);
}
.grid-project-row {
    background-color: rgba(0, 0, 0, 0.03);
    border-radius: 4px;
    padding: 4px 0;
}
[data-theme="dark"] .grid-project-row {
    background-color: rgba(255, 255, 255, 0.05);
}

@media (min-width: 768px) {
    ::v-deep .modal-dialog-elevated {
    margin-top: 5vh !important;
}
}

.delete-branch-details {
    background-color: var(--vt-card-bg, #f8f9fa) !important;
    border-color: var(--vt-border, #dee2e6) !important;
}

[data-theme="dark"] .delete-branch-details {
    background-color: rgba(255, 255, 255, 0.04) !important;
    border-color: rgba(255, 255, 255, 0.12) !important;
}

.ahead-commits-card {
    background-color: var(--vt-card-bg, #f8f9fa) !important;
    border-color: var(--vt-border, #dee2e6) !important;
}

[data-theme="dark"] .ahead-commits-card {
    background-color: rgba(255, 255, 255, 0.04) !important;
    border-color: rgba(255, 255, 255, 0.12) !important;
}

.ahead-commits-list {
    max-height: 220px;
    overflow-y: auto;
}

.ahead-commits-list--modal {
    max-height: 340px;
}

.branch-diff-pill {
    cursor: pointer;
    padding: 2px 7px;
    border-radius: 999px;
    border: 1px solid transparent;
    transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
    user-select: none;
}

.branch-diff-ahead {
    background-color: rgba(40, 167, 69, 0.08);
    border-color: rgba(40, 167, 69, 0.22);
}

.branch-diff-ahead:hover,
.branch-diff-ahead:focus {
    background-color: rgba(40, 167, 69, 0.18);
    border-color: rgba(40, 167, 69, 0.45);
    outline: none;
}

.branch-diff-behind {
    background-color: rgba(220, 53, 69, 0.08);
    border-color: rgba(220, 53, 69, 0.22);
}

.branch-diff-behind:hover,
.branch-diff-behind:focus {
    background-color: rgba(220, 53, 69, 0.18);
    border-color: rgba(220, 53, 69, 0.45);
    outline: none;
}

.commits-modal-tabs {
    background-color: rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 0, 0, 0.08);
}

[data-theme="dark"] .commits-modal-tabs {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
}

.commits-tab-btn {
    border: none;
    background: transparent;
    color: var(--text-muted, #6c757d);
    font-weight: 600;
    border-radius: 6px;
    padding: 4px 10px;
    transition: all 0.15s ease;
}

.commits-tab-btn:hover {
    color: var(--text-body, #212529);
    background-color: rgba(0, 0, 0, 0.04);
}

[data-theme="dark"] .commits-tab-btn:hover {
    color: #f8fafc;
    background-color: rgba(255, 255, 255, 0.06);
}

.commits-tab-btn.active {
    background-color: var(--vt-surface, #ffffff);
    color: var(--text-body, #212529);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

[data-theme="dark"] .commits-tab-btn.active {
    background-color: rgba(30, 41, 59, 0.95);
    color: #f8fafc;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}

.ahead-commit-row {
    border-radius: 6px;
    margin-bottom: 2px;
    background-color: rgba(0, 0, 0, 0.02);
    transition: background-color 0.15s ease;
}

[data-theme="dark"] .ahead-commit-row {
    background-color: rgba(255, 255, 255, 0.02);
}

.ahead-commit-row:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

[data-theme="dark"] .ahead-commit-row:hover {
    background-color: rgba(255, 255, 255, 0.06);
}

.branches-sprint-banner {
    border-radius: 6px;
    font-size: 0.8125rem;
    line-height: 1.45;
}

.branches-sprint-banner code {
    font-size: 0.775rem;
    padding: 1px 4px;
    border-radius: 3px;
    background-color: rgba(0, 0, 0, 0.06);
    color: inherit;
}

[data-theme="dark"] .branches-sprint-banner code {
    background-color: rgba(255, 255, 255, 0.12);
    color: #e0f2fe;
}

.btn-sprint-info-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 31px;
    width: 31px;
    padding: 0;
    font-size: 0.95rem;
    border-radius: 6px;
    transition: all 0.15s ease;
}

.mobile-col-caption {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted, #6c757d);
    margin-bottom: 0.2rem;
}

@media (max-width: 991.98px) {
    .grid-shell .time-grid-content > div > .card {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
    }

    .grid-project-row {
        border-radius: 0.65rem !important;
        padding: 0.5rem 0.75rem !important;
        background: rgba(15, 23, 42, 0.75) !important;
        border: 1px solid rgba(255, 255, 255, 0.08) !important;
        margin-bottom: 0.5rem !important;
    }

    [data-theme="light"] .grid-project-row {
        background: #f8fafc !important;
        border: 1px solid rgba(0, 0, 0, 0.06) !important;
    }

    .repo-branch-row {
        display: flex !important;
        flex-wrap: wrap !important;
        align-items: center !important;
        border-radius: 0.5rem !important;
        padding: 0.4rem 0.65rem !important;
        background: rgba(30, 41, 59, 0.65) !important;
        border: 1px solid rgba(255, 255, 255, 0.08) !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18) !important;
        margin-bottom: 0.35rem !important;
        row-gap: 0.15rem !important;
        column-gap: 0.45rem !important;
    }

    [data-theme="light"] .repo-branch-row {
        background: #ffffff !important;
        border: 1px solid rgba(0, 0, 0, 0.08) !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
    }

    .repo-branch-row > [class*="col"] {
        flex: 0 1 auto !important;
        width: auto !important;
        max-width: 100% !important;
        padding: 0 !important;
    }

    .repo-branch-row .repo-col-spacer {
        display: none !important;
    }

    /* Line 1: Repo pill, Branch Name, Diff, Status, Action Button */
    .repo-branch-row .repo-col-repo {
        order: 1 !important;
        font-size: 0.8rem !important;
    }

    .repo-branch-row .repo-col-name {
        order: 2 !important;
        font-size: 0.88rem !important;
        line-height: 1.2 !important;
    }

    .repo-branch-row .repo-col-diff {
        order: 3 !important;
        font-size: 0.8rem !important;
    }

    .repo-branch-row .repo-col-status {
        order: 4 !important;
        font-size: 0.75rem !important;
    }

    .repo-branch-row .repo-col-buttons {
        order: 5 !important;
        margin-left: auto !important;
    }

    /* Forced line break between Line 1 and Line 2 */
    .repo-branch-row::after {
        content: "" !important;
        order: 9 !important;
        flex-basis: 100% !important;
        width: 100% !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    /* Line 2: Author + Relative Time + Commit Message */
    .repo-branch-row .repo-col-author {
        order: 10 !important;
        font-size: 0.76rem !important;
        color: #94a3b8 !important;
        display: inline-flex !important;
        align-items: center !important;
    }

    [data-theme="light"] .repo-branch-row .repo-col-author {
        color: #64748b !important;
    }

    .repo-branch-row .repo-col-author::after {
        content: "•";
        margin-left: 0.35rem;
        margin-right: 0.15rem;
        color: #64748b !important;
    }

    [data-theme="light"] .repo-branch-row .repo-col-author::after {
        color: #94a3b8 !important;
    }

    .repo-branch-row .repo-col-commit {
        order: 11 !important;
        flex: 1 1 auto !important;
        width: auto !important;
        max-width: 100% !important;
        margin: 0 !important;
        font-size: 0.76rem !important;
        line-height: 1.25 !important;
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        justify-content: flex-start !important;
        overflow: hidden !important;
    }

    .repo-branch-row .repo-col-commit .mobile-col-caption {
        display: none !important;
    }

    .repo-branch-row .repo-col-commit > div.text-truncate {
        display: inline-flex !important;
        flex-direction: row !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 0.3rem !important;
        max-width: 100% !important;
        width: auto !important;
    }

    .repo-branch-row .repo-col-commit > div.text-truncate > span.small {
        font-size: 0.76rem !important;
        flex-shrink: 0 !important;
    }

    .repo-branch-row .repo-col-commit > div.text-truncate > div.text-muted {
        font-size: 0.74rem !important;
        line-height: 1.2 !important;
        display: inline-block !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        max-width: 480px !important;
    }

    .repo-branch-row .repo-col-commit > div.text-truncate > div.text-muted::before {
        content: "•";
        margin-right: 0.3rem;
        color: #64748b !important;
    }

    [data-theme="light"] .repo-branch-row .repo-col-commit > div.text-truncate > div.text-muted::before {
        color: #94a3b8 !important;
    }

    /* Suppress duplicate Default badge and floating dashes on mobile */
    .repo-branch-row.is-default .repo-col-diff,
    .repo-branch-row.is-default .repo-col-status {
        display: none !important;
    }

    .repo-branch-row.is-default .repo-col-buttons {
        display: none !important;
    }
}

.ahead-commit-row--mine {
    border-left: 3px solid #0284c7;
    background-color: rgba(2, 132, 199, 0.06);
}
[data-theme="dark"] .ahead-commit-row--mine {
    border-left-color: #38bdf8;
    background-color: rgba(56, 189, 248, 0.1);
}
.ahead-commit-row--other {
    opacity: 0.78;
}
.pr-row--abandoned {
    border-left: 3px solid #dc3545;
    background-color: rgba(220, 53, 69, 0.08);
}
[data-theme="dark"] .pr-row--abandoned {
    border-left-color: #f87171;
    background-color: rgba(248, 113, 113, 0.12);
}
.branch-compare-accordion {
    max-height: 360px;
    overflow-y: auto;
}
.branch-compare-file-item {
    border-color: var(--vt-border, #dee2e6) !important;
}
[data-theme="dark"] .branch-compare-file-item {
    border-color: rgba(255, 255, 255, 0.12) !important;
}
.branch-compare-file-header {
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0.03);
    transition: background-color 0.15s ease;
    user-select: none;
}
[data-theme="dark"] .branch-compare-file-header {
    background-color: rgba(255, 255, 255, 0.05);
}
.branch-compare-file-header:hover {
    background-color: rgba(0, 0, 0, 0.06);
}
[data-theme="dark"] .branch-compare-file-header:hover {
    background-color: rgba(255, 255, 255, 0.09);
}
.diff-table-wrap {
    max-height: 240px;
    overflow: auto;
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.72rem;
    line-height: 1.35;
}
.diff-code-table {
    border-collapse: collapse;
    table-layout: fixed;
}
.diff-code-table td {
    padding: 1px 6px;
    vertical-align: top;
}
.diff-code-table pre {
    font-family: inherit;
    font-size: inherit;
    color: inherit;
    white-space: pre-wrap;
    word-break: break-all;
}
.diff-line-num {
    width: 34px;
    text-align: right;
    color: var(--text-muted, #6c757d);
    user-select: none;
    opacity: 0.7;
    background-color: rgba(0, 0, 0, 0.02);
}
[data-theme="dark"] .diff-line-num {
    background-color: rgba(255, 255, 255, 0.02);
}
.diff-line-prefix {
    width: 18px;
    text-align: center;
    user-select: none;
    font-weight: 700;
}
.diff-line--add {
    background-color: rgba(40, 167, 69, 0.14);
    color: #14532d;
}
[data-theme="dark"] .diff-line--add {
    background-color: rgba(34, 197, 94, 0.18);
    color: #bbf7d0;
}
.diff-line--del {
    background-color: rgba(220, 53, 69, 0.14);
    color: #7f1d1d;
}
[data-theme="dark"] .diff-line--del {
    background-color: rgba(239, 68, 68, 0.18);
    color: #fecaca;
}
.diff-line--empty {
    background-color: rgba(128, 128, 128, 0.06);
}
.diff-code-table--split .diff-split-cell {
    width: calc(50% - 34px);
}
.repo-mobile-noop {
}
</style>
